import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router";
import {
  CaptureUpdateAction,
  CommandPalette,
  Excalidraw,
  useHandleLibrary,
} from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import type { ExcalidrawImperativeAPI, AppState, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import type {
  AiGrantAccess,
  CollaborationParticipant,
  DrawingAccessRole,
  DrawingShareReadResponse,
  FeedbackCategory,
  ExcalidrawSceneData,
  SaveStatus,
  SceneMode,
} from "@excalidraw-ms/shared";
import {
  COLLAB_AUTOSAVE_DELAY_MS,
  canonicalSceneJson,
  fingerprintCollabElements,
  shouldDeferAutosave,
} from "@excalidraw-ms/shared";

import { useAuth, didExplicitlySignOut, trySsoSilent } from "../auth/msalClient";
import { useAppActivity } from "../hooks/useAppActivity";
import * as backendClient from "../api/backendClient";
import {
  restoreScene,
  serializeFromAPI,
  serializeScene,
  emptyScene,
  sceneVersion,
  currentSceneVersion,
  repairSceneOrder,
} from "../utils/sceneHelpers";
import { captureCollabSnapshot, isCurrentCollabSnapshotSaved } from "../utils/collabSnapshot";
import {
  canWriteSceneStorage,
  hasLocalSceneDraft,
  recoveryStorageKey,
  sceneOnlyStorageKey,
  shouldTreatAsOffline,
  subscribeToSceneWriterInvalidation,
  writeSceneStorage,
} from "../utils/storageRecovery";
import {
  resolveStopSessionCase,
  type StopSessionCase,
  type StopSessionChoice,
} from "../components/stopSessionCopy";
import CustomMainMenu from "../components/CustomMainMenu";
import EditorSidePanel from "../components/EditorSidePanel";
import type { SupportedPreference, SupportedPreferenceState } from "../components/CustomMainMenu";
import { Button } from "@excalidraw-ms/shared-ui";
import CustomWelcomeScreen from "../components/CustomWelcomeScreen";
import BrandIcon from "../components/BrandIcon";
import UserProfileMenu from "../components/UserProfileMenu";
import HomeButton from "../components/HomeButton";
import SaveStatusIndicator from "../components/SaveStatusIndicator";
import DataClassificationBanner from "../components/DataClassificationBanner";
import BackendAccessDeniedNotice from "../components/BackendAccessDeniedNotice";
import FullAccessDialog from "../components/FullAccessDialog";
import { AnnouncementBanner } from "../components/AnnouncementBanner";
import { useAnnouncements } from "../hooks/useAnnouncements";
import { useHelpDialogLinks } from "../hooks/useHelpDialogLinks";
import { useThemePreference } from "../hooks/useThemePreference";
import { useDrawingAiSharing } from "../hooks/useDrawingAiSharing";
import { useDrawingReference } from "../hooks/useDrawingReference";
import { PeopleLinkPreparationError, readPeopleSharing } from "../utils/readPeopleSharing";
import { copyLink } from "../utils/copyLink";
import ShareButton from "../components/ShareButton";
import CollabShareDialog from "../components/CollabShareDialog";
import ShareableLinkDialog from "../components/ShareableLinkDialog";
import ShareWithAiDialog from "../components/ShareWithAiDialog";
import GalleryShareDialog from "../components/GalleryShareDialog";
import LoadFromLinkDialog from "../components/LoadFromLinkDialog";
import AboutDialog from "../components/AboutDialog";
import AiConnectDialog from "../components/AiConnectDialog";
import AgencyConnectDialog from "../components/AgencyConnectDialog";
import CreateWithAiDialog from "../components/CreateWithAiDialog";
import FeedbackDialog from "../components/FeedbackDialog";
import DevToolsPanel from "../components/DevToolsPanel";
import { useDevToolsAvailability } from "../hooks/useDevToolsAvailability";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import SaveToCloudDialog from "../components/SaveToCloudDialog";
import RenameDrawingDialog from "../components/RenameDrawingDialog";
import SaveToCloudExportCard from "../components/SaveToCloudExportCard";
import ShareableLinkExportCard from "../components/ShareableLinkExportCard";
import SaveConflictDialog from "../components/SaveConflictDialog";
import RecoveryDialog from "../components/RecoveryDialog";
import DestructiveConfirmationDialog from "../components/DestructiveConfirmationDialog";
import DrawingLoadErrorView from "../components/DrawingLoadErrorView";
import type { UnsavedAction } from "../components/UnsavedChangesDialog";
import { CollabClient } from "../collab/collabClient";
import { shouldAutoJoinFileCollaboration } from "../collab/shouldAutoJoin";
import { clearStagedImportedScene, consumeImportedScene } from "../utils/importScene";
import { captureAppScreenshot } from "../utils/captureAppScreenshot";
import { createDrawingPreview } from "../utils/createDrawingPreview";
import { cachePreviewFromBase64 } from "../utils/previewCache";
import { useExcalidrawWebMCP } from "../webmcp";
import { useApplicationWebMCP } from "../webmcp/useApplicationWebMCP";
import { ApplicationToolError } from "../webmcp/applicationTools";
import type { ApplicationResult } from "../webmcp/applicationTools";
import {
  useGalleryData,
  buildGalleryTabs,
  clearGalleryCache,
  isDrawingItem,
} from "../hooks/useGalleryData";
import type { AiShareTarget, DrawingItem } from "../hooks/useGalleryData";
import { FEEDBACK_TEAMS_CHAT_URL } from "../env";

const LOCAL_SCENE_SAVE_DELAY = 300; // 300ms (matches Excalidraw local save cadence)
const SAVE_REQUEST_SYNC_TIMEOUT_MS = 2_000;

type DrawingLoadError = {
  kind: "invalid-scene" | "generic";
  message: string;
  diagnostic?: string;
};

type InvalidSceneRecovery = {
  original: ExcalidrawSceneData;
  canRepairOrder: boolean;
};

function isInvalidSceneError(error: unknown): boolean {
  const message =
    typeof error === "object" && error !== null && "message" in error
      ? String((error as { message?: unknown }).message ?? "")
      : String(error ?? "");
  const backendMessage =
    error instanceof backendClient.BackendError ? (error.backendMessage ?? "") : "";
  return /invalid order key|stored drawing is invalid|invalid scene|not valid excalidraw/i.test(
    `${message}\n${backendMessage}`,
  );
}

async function waitForRequestedSceneVersion(
  api: ExcalidrawImperativeAPI,
  requestedVersion: number,
  requestedFingerprint?: string,
): Promise<boolean> {
  const deadline = Date.now() + SAVE_REQUEST_SYNC_TIMEOUT_MS;
  do {
    if (
      currentSceneVersion(api) >= requestedVersion &&
      (!requestedFingerprint ||
        (await fingerprintCollabElements(api.getSceneElementsIncludingDeleted())) ===
          requestedFingerprint)
    ) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  } while (Date.now() < deadline);
  return false;
}

// ---------------------------------------------------------------------------
// EditorPage — main orchestration component
// ---------------------------------------------------------------------------

export default function EditorPage() {
  const location = useLocation();
  const { drawingId: routeDrawingId, inviteToken: routeInviteToken } = useParams<{
    drawingId: string;
    inviteToken: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get("share") ?? undefined;
  const importShareBlobId = searchParams.get("importShare") ?? undefined;
  const isReadOnlyShare = searchParams.get("viewOnly") === "1";
  const canonicalDrawingId = routeDrawingId ?? searchParams.get("drawingId") ?? undefined;
  const createDrawingOperationIdRef = useRef(crypto.randomUUID());
  const deleteOperationIdsRef = useRef(new Map<string, string>());
  const [speCoordinates, setSpeCoordinates] = useState<{
    driveId: string;
    itemId: string;
  } | null>(null);
  const driveId = speCoordinates?.driveId;
  const itemId = speCoordinates?.itemId;
  const isSpeDrawing = !!canonicalDrawingId;

  // Is this an ephemeral room join via /join/:inviteToken?
  const isEphemeralJoin = !!routeInviteToken;

  // Auth
  const {
    account,
    setAccount,
    loading: authLoading,
    signIn,
    signOut: doSignOut,
    getToken,
    hasBackendAccess,
    backendAccessLoading,
    backendAccessDenied,
    backendCapabilityState: authBackendCapabilityState,
    backendAccessEligibilityState: authBackendAccessEligibilityState,
    backendAccessPhase,
    retryBackendAccess,
    canViewImpactDashboard,
  } = useAuth();
  const backendCapabilityState =
    authBackendCapabilityState ??
    (hasBackendAccess ? "full" : backendAccessDenied ? "local-only" : "checking");
  const backendAccessEligibilityState =
    authBackendAccessEligibilityState ??
    (backendCapabilityState === "full"
      ? "granted"
      : backendCapabilityState === "local-only"
        ? "denied"
        : "checking");

  const { announcement, dismiss: dismissAnnouncement } = useAnnouncements({
    accountId: account?.homeAccountId,
    capabilityState: backendCapabilityState,
  });
  const {
    preference: themePreference,
    resolvedTheme,
    setPreference: setThemePreference,
  } = useThemePreference({
    accountId: account?.homeAccountId,
    getToken,
    syncEnabled: hasBackendAccess,
  });

  // Excalidraw API handle
  const [excalidrawAPI, setExcalidrawAPI] = useState<ExcalidrawImperativeAPI | null>(null);
  const onExcalidrawAPI = useCallback((api: ExcalidrawImperativeAPI) => setExcalidrawAPI(api), []);

  // Wire up the library install flow. When a library link redirects back to
  // the editor with a `#addLibrary=...` hash, this hook reads the hash,
  // fetches the .excalidrawlib file, prompts the user, and installs it via
  // `excalidrawAPI.updateLibrary(...)`. Without it, the hash is silently
  // ignored and "Browse libraries" -> Install does nothing.
  useHandleLibrary({ excalidrawAPI });

  // Is this a new (unsaved) drawing? Only canonical SPE routes are file-backed.
  const isNewDrawing = !isSpeDrawing;

  // Scene mode: "scene-only" (no file) or "file-backed" (UX rules 20–26)
  const sceneMode: SceneMode = isNewDrawing ? "scene-only" : "file-backed";
  const isFileBacked = sceneMode === "file-backed";

  // Scene state
  const [initialData, setInitialData] = useState<{
    elements: ExcalidrawElement[];
    appState: Partial<AppState>;
    files: BinaryFiles;
  } | null>(null);
  const [sceneLoading, setSceneLoading] = useState(!isNewDrawing);
  const [error, setError] = useState<DrawingLoadError | null>(null);
  const [copyDetailsError, setCopyDetailsError] = useState<string | null>(null);
  const [sceneLoadAttempt, setSceneLoadAttempt] = useState(0);
  const recordAppActivity = useAppActivity({
    accountId: account?.homeAccountId,
    enabled: hasBackendAccess && !authLoading,
    getToken,
    drawingReady:
      location.state?.appUsageAutomated !== true &&
      !!excalidrawAPI &&
      !sceneLoading &&
      !error &&
      (!isNewDrawing || !!initialData?.elements.some((element) => !element.isDeleted)),
  });
  const [invalidSceneRecovery, setInvalidSceneRecovery] = useState<InvalidSceneRecovery | null>(
    null,
  );
  const [drawingRole, setDrawingRole] = useState<"owner" | "viewer" | "editor">("owner");
  /** Role held in the current scene-only room. Meaningless in file-backed mode. */
  const [ephemeralAccessLevel, setEphemeralAccessLevel] = useState<"editor" | "viewer">("editor");
  const isViewModeEnforced =
    isReadOnlyShare || drawingRole === "viewer" || ephemeralAccessLevel === "viewer";
  /** Whether this client started the current scene-only room, and so may share it. */
  const [isEphemeralRoomOwner, setIsEphemeralRoomOwner] = useState(false);
  // Invitations are owner-only; cloud recipients may copy existing-access references.
  const canCreateLiveShareLink = isFileBacked ? drawingRole === "owner" : isEphemeralRoomOwner;
  const isCloudRecipient = isFileBacked && drawingRole !== "owner";

  // Collab state
  const collabRef = useRef<CollabClient | null>(null);
  const [isCollaborating, setIsCollaborating] = useState(false);
  const [liveParticipants, setLiveParticipants] = useState<readonly CollaborationParticipant[]>([]);
  const [participantsConfirmed, setParticipantsConfirmed] = useState(false);
  const sponsorOid =
    typeof account?.idTokenClaims?.oid === "string"
      ? account.idTokenClaims.oid.toLowerCase()
      : account?.localAccountId?.toLowerCase();
  const ownAiParticipants = liveParticipants.filter(
    (participant) => participant.identity?.sponsorUserId.toLowerCase() === sponsorOid,
  );
  const drawingAiSharing = useDrawingAiSharing({
    drawingId: canonicalDrawingId,
    accountId: account?.homeAccountId ?? account?.username,
    enabled: hasBackendAccess && !sceneLoading && !error && !!driveId && !!itemId,
    getToken,
  });
  const [hasPeopleSharing, setHasPeopleSharing] = useState<boolean | undefined>(undefined);
  const [peopleSharingKind, setPeopleSharingKind] = useState<
    DrawingShareReadResponse["peopleSharingKind"] | undefined
  >(undefined);
  const [hasBroadCollaborationSharing, setHasBroadCollaborationSharing] = useState(false);
  const [showDrawingAiRevoke, setShowDrawingAiRevoke] = useState(false);
  const [drawingAiRevoking, setDrawingAiRevoking] = useState(false);
  const [drawingAiRevokeError, setDrawingAiRevokeError] = useState<string | null>(null);
  const [aiConnectionCleanupPending, setAiConnectionCleanupPending] = useState(false);
  const observedExternalAiRevocation = useRef(0);

  // Save state (P4)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveConflictOpen, setSaveConflictOpen] = useState(false);
  const [saveConflictBusy, setSaveConflictBusy] = useState(false);
  const [saveConflictError, setSaveConflictError] = useState<string | null>(null);
  useEffect(() => {
    if (isCollaborating) setSaveConflictOpen(false);
  }, [isCollaborating]);
  const [recoveryBackup, setRecoveryBackup] = useState<{
    key: string;
    serializedScene: string;
  } | null>(null);
  const currentETagRef = useRef<string | undefined>(undefined); // React 19: useRef requires an explicit initial value
  const lastSaveVersionRef = useRef<number>(0);
  // Suppress dirty-tracking until Excalidraw settles with the loaded scene.
  // Excalidraw's internal restore may bump element versions beyond what our
  // restoreScene() produced, causing a false "unsaved" immediately after load.
  const suppressDirtyUntilSettledRef = useRef<boolean>(!isNewDrawing);
  const lastRemoteUpdateTimeRef = useRef<number>(0);
  const lastSaveTimeRef = useRef<number>(Date.now());
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sceneOnlySaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveryCheckedRef = useRef<string | null>(null);
  const activeCollaboratorsOnLoadRef = useRef<number>(0);
  const autoJoinFileCollaborationRef = useRef(false);
  const handleSaveRef = useRef<(requestIds?: string[]) => Promise<boolean>>(async () => false);
  const saveInFlightRef = useRef(false);
  const localSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const delegatedSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const pendingLeaderSaveRequestIdsRef = useRef(new Set<string>());
  const guardedNavigateAwayRef = useRef<() => void>(() => {});
  const saveAttemptRef = useRef(0);
  const skipBeforeUnloadRef = useRef(false);

  // Number of users already in the room (shown on Collab button before joining)
  const [roomActiveCount, setRoomActiveCount] = useState(0);

  // SPE/Cosmos is the only cloud-storage path. Security group membership
  // (the `Excalidraw.User` app role behind `hasBackendAccess`) is the sole gate.
  const speAvailable = hasBackendAccess;
  const cloudFeaturesVisible = backendAccessEligibilityState === "granted";
  const cloudExportDisabledReason =
    backendCapabilityState === "checking"
      ? backendAccessPhase === "preparing-cloud"
        ? "Preparing cloud features…"
        : "Checking cloud availability…"
      : "Cloud features are temporarily unavailable. Try again from the notice below.";
  const showDisabledShareableLink = cloudFeaturesVisible && backendCapabilityState !== "full";
  const showSaveToCloudCard = cloudFeaturesVisible && isNewDrawing && !isCollaborating;

  // Gallery data comes exclusively from Cosmos metadata.
  const galleryData = useGalleryData(
    getToken,
    isNewDrawing && speAvailable,
    account?.homeAccountId,
  );
  const galleryTabs = cloudFeaturesVisible
    ? buildGalleryTabs(galleryData.recentCount, galleryData.sharedCount, galleryData.aiCount)
    : [];

  const handleGalleryItemClick = useCallback(
    (item: DrawingItem) => {
      navigate(`/drawing/${encodeURIComponent(item.drawingId)}`);
    },
    [navigate],
  );

  const [drawingPendingDelete, setDrawingPendingDelete] = useState<DrawingItem | null>(null);
  const handleGalleryItemDuplicate = useCallback(
    async (item: DrawingItem) => {
      if (item.role !== "owner" || !speAvailable) return;
      const operationId = item.copySourceDrawingId ? item.drawingId : galleryData.startCopy(item);
      const sourceDrawingId = item.copySourceDrawingId ?? item.drawingId;
      if (item.copySourceDrawingId) galleryData.retryCopy(operationId);
      try {
        const token = await getToken();
        const result = await backendClient.duplicateDrawing(token, sourceDrawingId, operationId);
        if (result.metadataStatus === "repair_pending") {
          galleryData.failCopy(
            operationId,
            "The copy was saved, but is still syncing.",
            true,
            true,
            result,
          );
          return;
        }
        galleryData.finishCopy(operationId, result);
      } catch (error) {
        console.error("[EditorPage] SPE drawing duplication failed:", error);
        if (error instanceof backendClient.BackendError) {
          const invalidScene = error.backendMessage === "Stored drawing is invalid";
          const retryable =
            error.status === 408 || error.status === 429 || (error.status >= 500 && !invalidScene);
          if (error.status === 404) {
            galleryData.failCopy(
              operationId,
              "This drawing is no longer available. My Drawings has been refreshed.",
              false,
              true,
            );
          } else if (error.status === 403) {
            galleryData.failCopy(
              operationId,
              "You no longer have access to make a copy of this drawing.",
              false,
            );
          } else if (invalidScene) {
            galleryData.failCopy(
              operationId,
              "Couldn't copy: the saved content or preview is invalid.",
              false,
            );
          } else if (error.backendMessage === "Copy is still being prepared") {
            galleryData.failCopy(operationId, "This copy is still being prepared.", true);
          } else if (
            error.backendMessage === "Drawing storage is unavailable" ||
            error.backendMessage === "Drawing metadata is unavailable"
          ) {
            galleryData.failCopy(
              operationId,
              "The drawing service is temporarily unavailable.",
              true,
            );
          } else if (error.backendMessage === "Copy could not be safely resumed") {
            galleryData.failCopy(
              operationId,
              "This copy could not be finished safely. Make a new copy from the original.",
              false,
            );
          } else if (retryable) {
            galleryData.failCopy(operationId, "The copy could not be completed right now.", true);
          } else {
            galleryData.failCopy(
              operationId,
              "The copy could not be completed. Make a new copy from the original.",
              false,
            );
          }
        } else {
          galleryData.failCopy(
            operationId,
            shouldTreatAsOffline(error)
              ? "You're offline. Reconnect to continue."
              : "The copy could not be completed. Make a new copy from the original.",
            shouldTreatAsOffline(error),
          );
        }
      }
    },
    [galleryData, getToken, speAvailable],
  );
  const handleGalleryItemDismissCopy = useCallback(
    (item: DrawingItem) => {
      galleryData.dismissCopy(item.drawingId);
      // Best effort: the reconciler abandons expired reservations if this call
      // fails. An already-published copy is preserved and restored by refresh.
      void getToken()
        .then((token) => backendClient.abandonDrawingCopy(token, item.drawingId))
        .then((result) => {
          if (result.status === "active") galleryData.refreshOwned();
        })
        .catch((error: unknown) => {
          console.error("[EditorPage] Failed to abandon copy reservation:", error);
        });
    },
    [galleryData, getToken],
  );
  const [drawingDeleteState, setDrawingDeleteState] = useState<"idle" | "deleting">("idle");
  const [drawingDeleteError, setDrawingDeleteError] = useState<string | null>(null);

  const handleGalleryItemDelete = useCallback((item: DrawingItem) => {
    if (item.role !== "owner") return;
    setDrawingDeleteError(null);
    setDrawingPendingDelete(item);
  }, []);

  const handleConfirmDrawingDelete = useCallback(async () => {
    if (!drawingPendingDelete || drawingDeleteState === "deleting") return;
    const operationId =
      deleteOperationIdsRef.current.get(drawingPendingDelete.drawingId) ?? crypto.randomUUID();
    deleteOperationIdsRef.current.set(drawingPendingDelete.drawingId, operationId);
    setDrawingDeleteState("deleting");
    setDrawingDeleteError(null);
    try {
      const token = await getToken();
      await backendClient.deleteDrawing(
        token,
        drawingPendingDelete.drawingId,
        operationId,
        collabRef.current?.getParticipantId() ?? undefined,
      );
      deleteOperationIdsRef.current.delete(drawingPendingDelete.drawingId);
      galleryData.removeItem(drawingPendingDelete.drawingId);
      setDrawingPendingDelete(null);
    } catch (err) {
      console.error("[EditorPage] SPE drawing deletion failed:", err);
      setDrawingDeleteError("The drawing could not be deleted. Please try again.");
    } finally {
      setDrawingDeleteState("idle");
    }
  }, [drawingDeleteState, drawingPendingDelete, galleryData, getToken]);

  const [drawingPendingRename, setDrawingPendingRename] = useState<DrawingItem | null>(null);
  const [drawingRenaming, setDrawingRenaming] = useState(false);
  const [drawingRenameError, setDrawingRenameError] = useState<string | null>(null);

  const handleGalleryItemRename = useCallback((item: DrawingItem) => {
    if (item.role !== "owner" && item.role !== "editor") return;
    setDrawingRenameError(null);
    setDrawingPendingRename(item);
  }, []);

  const handleConfirmDrawingRename = useCallback(
    async (name: string) => {
      if (!drawingPendingRename || drawingRenaming) return;
      setDrawingRenaming(true);
      setDrawingRenameError(null);
      try {
        const token = await getToken();
        const result = await backendClient.renameDrawing(
          token,
          drawingPendingRename.drawingId,
          name,
        );
        galleryData.renameItem(
          drawingPendingRename.drawingId,
          result.name ?? name,
          result.updatedAt,
        );
        setDrawingPendingRename(null);
      } catch (error) {
        console.error("[EditorPage] SPE drawing rename failed:", error);
        const status = error instanceof backendClient.BackendError ? error.status : undefined;
        setDrawingRenameError(
          status === 403
            ? "You no longer have permission to rename this drawing."
            : status === 404
              ? "This drawing is no longer available."
              : shouldTreatAsOffline(error)
                ? "You're offline. Reconnect to rename this drawing."
                : "The drawing could not be renamed. Please try again.",
        );
      } finally {
        setDrawingRenaming(false);
      }
    },
    [drawingPendingRename, drawingRenaming, galleryData, getToken],
  );

  // AI grant revocation (design D9). Terminal: a revoked code is dead forever,
  // so it is gated behind an explicit confirmation. Revoking removes only the
  // grant row — the drawing itself is untouched and stays in the other tabs.
  const [aiGrantPendingRevoke, setAiGrantPendingRevoke] = useState<AiShareTarget | null>(null);
  const [aiGrantRevokeState, setAiGrantRevokeState] = useState<"idle" | "revoking">("idle");
  const [aiGrantRevokeError, setAiGrantRevokeError] = useState<string | null>(null);

  const handleGalleryItemRevokeAi = useCallback((item: AiShareTarget) => {
    if (!item.aiGrantCode) return;
    setAiGrantRevokeError(null);
    setAiGrantPendingRevoke(item);
  }, []);

  // "Share with AI" is a permission decision, so it gets a dialog — the same
  // shape as sharing with a person (design D9, ux-rules § 15f). The card only
  // names the drawing; everything else happens here.
  const [aiShareTarget, setAiShareTarget] = useState<AiShareTarget | null>(null);
  const [galleryShareTarget, setGalleryShareTarget] = useState<DrawingItem | null>(null);
  // The freshly minted grant, so the dialog can show the code immediately even
  // when the share came from the main menu and the gallery is not mounted.
  const [aiShareResult, setAiShareResult] = useState<{
    code: string;
    access: AiGrantAccess;
  } | null>(null);

  // Mint the grant for the drawing the dialog is open on. The backend is
  // idempotent while active, so re-sharing returns the same code. We record the
  // grant so the "Shared with AI" tab, its count, and the card's label stay in
  // sync. The response's access is what the user can do — reported, not chosen.
  const handleShareAi = useCallback(
    async (target: AiShareTarget): Promise<void> => {
      const token = await getToken();
      const { grant, access } = await backendClient.mintAiGrant(token, target.drawingId);
      setAiShareResult({ code: grant.code, access });
      // Only a real gallery card can join the gallery's own lists. Sharing from
      // the menu still mints; the AI tab picks the grant up from listAiGrants
      // the next time it loads.
      if (isDrawingItem(target)) galleryData.addAiGrant(target, grant, access);
    },
    [galleryData, getToken],
  );

  const handleConfirmAiGrantRevoke = useCallback(async () => {
    const code = aiGrantPendingRevoke?.aiGrantCode;
    if (!code || aiGrantRevokeState === "revoking") return;
    setAiGrantRevokeState("revoking");
    setAiGrantRevokeError(null);
    try {
      const token = await getToken();
      await backendClient.revokeAiGrant(token, code);
      galleryData.removeAiGrant(code);
      setAiGrantPendingRevoke(null);
    } catch (err) {
      console.error("[EditorPage] AI grant revocation failed:", err);
      setAiGrantRevokeError("AI access could not be turned off. Please try again.");
    } finally {
      setAiGrantRevokeState("idle");
    }
  }, [aiGrantPendingRevoke, aiGrantRevokeState, galleryData, getToken]);

  // Register the supported browser WebMCP tools.
  const [ssoInProgress, setSsoInProgress] = useState(false);
  useExcalidrawWebMCP({
    getToken,
    excalidrawAPI:
      sceneLoading || authLoading || ssoInProgress || !account || error ? null : excalidrawAPI,
    isReadOnly: isViewModeEnforced,
    driveId: driveId ?? "",
    itemId: itemId ?? "",
    drawingId: canonicalDrawingId ?? "",
    canvasIdentity: `${account?.homeAccountId ?? ""}:${canonicalDrawingId ?? "local"}:${searchParams.toString()}`,
    legacyStorageDisabled: true,
  });

  // Clean up _ts and import query params after mount
  useEffect(() => {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has("_ts")) {
      url.searchParams.delete("_ts");
      changed = true;
    }
    if (url.searchParams.has("import")) {
      url.searchParams.delete("import");
      changed = true;
    }
    if (changed) {
      window.history.replaceState(null, "", url.pathname + url.search);
    }
  }, []);

  // WelcomeScreen visibility (only for new solo drawings — not ephemeral joins)
  const [showWelcome, setShowWelcome] = useState(!isEphemeralJoin);

  // Current file name for status and collaboration UI.
  const currentFileNameRef = useRef<string | undefined>(undefined); // React 19: useRef requires an explicit initial value

  // Share / collab dialogs
  const [showCollabShareDialog, setShowCollabShareDialog] = useState(false);
  const drawingReference = useDrawingReference({
    drawingId: canonicalDrawingId,
    accountId: account?.homeAccountId ?? account?.username,
    enabled: showCollabShareDialog && isCloudRecipient && !sceneLoading && hasBackendAccess,
    getToken,
  });
  const [shareableLinkUrl, setShareableLinkUrl] = useState<string | null>(null);
  const [activeShareUrl, setActiveShareUrl] = useState<string | null>(null);
  const [linkRole, setLinkRole] = useState<DrawingAccessRole>("editor");
  const [linkRoleBusy, setLinkRoleBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [peopleAccessLoading, setPeopleAccessLoading] = useState(false);
  const [peopleAccessError, setPeopleAccessError] = useState<string | null>(null);
  const [peopleAccessRevision, setPeopleAccessRevision] = useState(0);
  const [stopCase, setStopCase] = useState<StopSessionCase>("cloud-copy");
  // Links already resolved for this drawing, keyed by role. Reused so toggling
  // the selector never mints a second URL for the same Graph permission.
  const shareUrlByRoleRef = useRef<Map<DrawingAccessRole, string>>(new Map());
  const shareUrlPromiseByRoleRef = useRef<Map<DrawingAccessRole, Promise<string>>>(new Map());
  const shareCacheGenerationRef = useRef(0);
  const linkResolutionRequestRef = useRef(0);
  const peoplePreparationRetryRef = useRef<(() => Promise<DrawingShareReadResponse>) | null>(null);
  const [peopleAccessRetryLabel, setPeopleAccessRetryLabel] = useState("Retry people access");
  const [showLoadFromLinkDialog, setShowLoadFromLinkDialog] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [showAiConnectDialog, setShowAiConnectDialog] = useState(false);
  const [showAiChooser, setShowAiChooser] = useState(false);
  const [showFullAccess, setShowFullAccess] = useState(false);
  const openFullAccess = useCallback(() => {
    setShowAiChooser(false);
    setShowFullAccess(true);
  }, []);
  const closeFullAccess = useCallback(() => setShowFullAccess(false), []);
  const [aiInitialStep, setAiInitialStep] = useState<1 | 2>(1);
  const [aiSetupHost, setAiSetupHost] = useState<"copilot" | "agency">("copilot");
  const AiSetupDialog = aiSetupHost === "agency" ? AgencyConnectDialog : AiConnectDialog;
  const openAiSetup = useCallback((step: 1 | 2, host: "copilot" | "agency" = "copilot") => {
    setShowAiChooser(false);
    setAiSetupHost(host);
    setAiInitialStep(step);
    setShowAiConnectDialog(true);
  }, []);
  const openAiChooser = useCallback(() => {
    setShowAiConnectDialog(false);
    setShowAiChooser(true);
  }, []);
  const closeAiSetup = useCallback(() => setShowAiConnectDialog(false), []);
  const closeAiChooser = useCallback(() => setShowAiChooser(false), []);
  const [showSaveToCloudDialog, setShowSaveToCloudDialog] = useState(false);
  const [saveToCloudError, setSaveToCloudError] = useState<string | null>(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const openFeedbackDialog = useCallback(() => setShowFeedbackDialog(true), []);
  useHelpDialogLinks({ onFeedback: openFeedbackDialog, teamsUrl: FEEDBACK_TEAMS_CHAT_URL });
  const [showDevToolsPanel, setShowDevToolsPanel] = useState(false);
  const devToolsAvailable = useDevToolsAvailability(!!account);
  // Tracks whether a native Excalidraw dialog (imageExport/jsonExport) is currently
  // open. When true, LoadFromLinkDialog steps back (lower z-index) so the native
  // dialog appears on top. Ref used in handleChange to avoid stale closure.
  const nativeDialogActiveRef = useRef(false);
  const drawingStartedTelemetrySentRef = useRef(false);
  const editorEngagedTelemetrySentRef = useRef(false);
  const [nativeDialogActive, setNativeDialogActive] = useState(false);
  const [pendingImportedScene, setPendingImportedScene] = useState<{
    elements: ExcalidrawElement[];
    appState: Partial<AppState>;
    files: BinaryFiles;
  } | null>(null);

  // -----------------------------------------------------------------------
  // 1. Auth: try silent SSO on launch (skip if user explicitly signed out)
  // -----------------------------------------------------------------------
  const ssoAttemptedRef = useRef(false);

  useEffect(() => {
    if (authLoading || account || ssoAttemptedRef.current) return;
    if (didExplicitlySignOut()) return; // User signed out — stay on gate

    ssoAttemptedRef.current = true;
    setSsoInProgress(true);
    trySsoSilent()
      .then((ssoAccount) => {
        if (ssoAccount) setAccount(ssoAccount);
      })
      .finally(() => setSsoInProgress(false));
  }, [authLoading, account, setAccount]);

  // -----------------------------------------------------------------------
  // 2. Initialise scene: blank canvas for /, load from backend otherwise
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!account) return;
    if (isNewDrawing) {
      // ⚠️ SECURITY: When joining a collab room (/join/:inviteToken), NEVER restore
      // local state. The room's scene is authoritative. Restoring a scene-only
      // draft or imported scene would leak private local data to all collaborators.
      if (isEphemeralJoin) {
        const restored = restoreScene(emptyScene());
        lastSaveVersionRef.current = sceneVersion(restored.elements as ExcalidrawElement[]);
        setInitialData(restored);
        setSceneLoading(false);
        return;
      }

      if (!canWriteSceneStorage()) {
        clearStagedImportedScene();
        const restored = restoreScene(emptyScene());
        lastSaveVersionRef.current = sceneVersion(restored.elements as ExcalidrawElement[]);
        setInitialData(restored);
        setSceneLoading(false);
        return;
      }

      // Check for imported scene from gallery "Load from file" action
      const importedJSON = consumeImportedScene();
      if (importedJSON) {
        try {
          const imported = restoreScene(JSON.parse(importedJSON));
          lastSaveVersionRef.current = sceneVersion(imported.elements as ExcalidrawElement[]);
          // Imported scene-only data has never been saved to a file — mark
          // dirty so the unsaved-changes guard protects it.
          setIsDirty(true);
          setInitialData(imported);
          setSceneLoading(false);
          return;
        } catch {
          // Fall through to normal blank/draft restore
        }
      }

      let restored = restoreScene(emptyScene());
      let restoredFromDraft = false;
      try {
        const raw = localStorage.getItem(sceneOnlyStorageKey());
        if (raw) {
          restored = restoreScene(JSON.parse(raw));
          restoredFromDraft = restored.elements.length > 0;
        }
      } catch {
        // Fall back to blank scene if browser storage is unavailable or corrupt
      }

      lastSaveVersionRef.current = sceneVersion(restored.elements as ExcalidrawElement[]);
      // A draft restored from browser cache has never been saved to a file —
      // mark dirty so the unsaved-changes guard protects against accidental loss.
      if (restoredFromDraft) {
        drawingStartedTelemetrySentRef.current = true;
        setIsDirty(true);
      }
      setInitialData(restored);
      setSceneLoading(false);
      return;
    }
    if (backendAccessLoading) return;
    if (!hasBackendAccess) {
      setSceneLoading(false);
      setError(null);
      return;
    }
    if (!isSpeDrawing) return;

    let cancelled = false;
    setSceneLoading(true);
    setError(null);
    setInvalidSceneRecovery(null);

    (async () => {
      let sceneRestorationFailed = false;
      try {
        const token = await getToken();
        const loaded = await backendClient.loadDrawing(token, canonicalDrawingId!);
        if (cancelled) return;

        currentETagRef.current = loaded.eTag;
        setSpeCoordinates({ driveId: loaded.driveId, itemId: loaded.itemId });
        currentFileNameRef.current = loaded.name ?? `Drawing ${loaded.drawingId.slice(0, 8)}`;
        activeCollaboratorsOnLoadRef.current = loaded.activeCollaborators;
        autoJoinFileCollaborationRef.current = shouldAutoJoinFileCollaboration(
          loaded.activeCollaborators,
          loaded.role,
          loaded.isShared,
        );
        setDrawingRole(loaded.role);
        setHasBroadCollaborationSharing(loaded.isShared);
        setHasPeopleSharing(loaded.role === "owner" ? undefined : true);
        setPeopleSharingKind(loaded.role === "owner" ? undefined : "recipient");
        setRoomActiveCount(loaded.activeCollaborators);

        if (loaded.scene) {
          let restored;
          try {
            restored = restoreScene(loaded.scene);
          } catch (restoreError) {
            sceneRestorationFailed = true;
            let canRepairOrder = false;
            try {
              restoreScene(repairSceneOrder(loaded.scene));
              canRepairOrder = true;
            } catch {
              // The original scene remains available for export when the probe fails.
            }
            setInvalidSceneRecovery({
              original: loaded.scene,
              canRepairOrder,
            });
            throw restoreError;
          }
          // Initialise save-version to the loaded scene so the first onChange
          // doesn't falsely mark the file dirty and trigger a spurious auto-save.
          lastSaveVersionRef.current = sceneVersion(restored.elements as ExcalidrawElement[]);
          setInitialData(restored);
          suppressDirtyUntilSettledRef.current = true;
        }
        setSaveStatus("saved");
      } catch (err: unknown) {
        if (cancelled) return;
        const httpErr = err as { status?: number };
        // 404 = hard error (drawing deleted or permission revoked), not a new drawing (R3-#8)
        const message = shouldTreatAsOffline(err)
          ? "You appear to be offline. Reconnect and reload this drawing."
          : httpErr.status === 404
            ? "The drawing was deleted or your permission changed."
            : httpErr.status === 403
              ? "You no longer have permission to open this drawing."
              : httpErr.status === 401
                ? "Your sign-in expired. Sign in again, then reopen this drawing."
                : "We couldn't load this drawing because the service is temporarily unavailable.";
        const invalidScene = sceneRestorationFailed || isInvalidSceneError(err);
        console.error("[EditorPage] Failed to load drawing:", err);
        setError({
          kind: invalidScene ? "invalid-scene" : "generic",
          message,
          diagnostic:
            invalidScene && canonicalDrawingId
              ? `INVALID_SCENE\nDrawing: ${canonicalDrawingId}\n${String(
                  err instanceof Error ? err.message : message,
                ).slice(0, 500)}`
              : undefined,
        });
      } finally {
        if (!cancelled) setSceneLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    account,
    canonicalDrawingId,
    isSpeDrawing,
    isNewDrawing,
    isEphemeralJoin,
    getToken,
    shareToken,
    navigate,
    hasBackendAccess,
    backendAccessLoading,
    sceneLoadAttempt,
  ]);

  const handleOpenRepairedCopy = useCallback(() => {
    if (!invalidSceneRecovery?.canRepairOrder) return;
    try {
      const repaired = restoreScene(repairSceneOrder(invalidSceneRecovery.original));
      lastSaveVersionRef.current = sceneVersion(repaired.elements as ExcalidrawElement[]);
      suppressDirtyUntilSettledRef.current = true;
      setInitialData(repaired);
      setSaveStatus("saved");
      setIsDirty(false);
      setError(null);
      setInvalidSceneRecovery(null);
    } catch (repairError) {
      console.error("[EditorPage] Failed to open repaired scene copy:", repairError);
      setInvalidSceneRecovery((current) =>
        current ? { ...current, canRepairOrder: false } : current,
      );
    }
  }, [invalidSceneRecovery]);

  const handleDownloadInvalidScene = useCallback(() => {
    if (!invalidSceneRecovery) return;
    const blob = new Blob([JSON.stringify(invalidSceneRecovery.original, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${currentFileNameRef.current ?? "invalid-drawing"}.original.excalidraw`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [invalidSceneRecovery]);

  // -----------------------------------------------------------------------
  // 2b. After Excalidraw mounts, recompute offsets to avoid misalignment
  // -----------------------------------------------------------------------
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!excalidrawAPI) return;
    const id = setTimeout(() => excalidrawAPI.refresh(), 300);
    const container = containerRef.current;
    let observer: ResizeObserver | undefined;
    if (container && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => excalidrawAPI.refresh());
      observer.observe(container);
    }
    return () => {
      clearTimeout(id);
      observer?.disconnect();
    };
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || sceneLoading || !driveId || !itemId) return;
    const key = recoveryStorageKey(driveId, itemId);
    if (recoveryCheckedRef.current === key) return;
    recoveryCheckedRef.current = key;

    if (!canWriteSceneStorage()) {
      setRecoveryBackup(null);
      return;
    }

    const backup = (() => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    })();
    if (!backup) return;
    if (!canWriteSceneStorage()) return;
    setRecoveryBackup({ key, serializedScene: backup });
  }, [driveId, itemId, excalidrawAPI, sceneLoading]);

  const handleRestoreRecovery = useCallback(() => {
    if (!recoveryBackup || !excalidrawAPI) return;
    try {
      if (!canWriteSceneStorage()) return;
      const parsed = JSON.parse(recoveryBackup.serializedScene);
      const restored = restoreScene(parsed);
      if (!canWriteSceneStorage()) return;
      excalidrawAPI.updateScene({
        elements: restored.elements,
        appState: restored.appState,
      });
      setIsDirty(true);
      setSaveStatus("unsaved");
    } catch (err) {
      console.error("[EditorPage] Failed to restore local recovery backup:", err);
    } finally {
      try {
        localStorage.removeItem(recoveryBackup.key);
      } catch {
        // The invalidation gate remains fail-closed when storage is unavailable.
      }
      setRecoveryBackup(null);
    }
  }, [excalidrawAPI, recoveryBackup]);

  const handleDiscardRecovery = useCallback(() => {
    if (!recoveryBackup) return;
    try {
      localStorage.removeItem(recoveryBackup.key);
    } catch {
      // Dismissing stale recovery UI does not depend on browser storage access.
    }
    setRecoveryBackup(null);
  }, [recoveryBackup]);

  // -----------------------------------------------------------------------
  // 3a. Start collab when API handle is ready (file-backed drawings)
  // -----------------------------------------------------------------------
  const collabAccountIdentity = account?.username ?? "";
  const collabDisplayNameRef = useRef("");
  collabDisplayNameRef.current = account?.name || collabAccountIdentity;

  useEffect(() => {
    if (!excalidrawAPI || !collabAccountIdentity || isNewDrawing || !driveId || !itemId) return;
    // Skip if this is an ephemeral room join — handled separately
    if (isEphemeralJoin) return;

    const client = new CollabClient({
      excalidrawAPI,
      getToken,
      driveId,
      itemId,
      drawingId: canonicalDrawingId,
      shareToken,
      username: collabDisplayNameRef.current,
      onConnectionChange: (connected) => {
        setIsCollaborating(connected);
        if (!connected) {
          setRoomActiveCount(0);
        }
      },
      onRoomUserCountChange: (count) => {
        setRoomActiveCount(count);
      },
      onParticipantsChange: (participants, confirmed) => {
        setLiveParticipants(participants);
        setParticipantsConfirmed(confirmed);
      },
      onETagUpdate: (eTag) => {
        currentETagRef.current = eTag;
        lastRemoteUpdateTimeRef.current = Date.now();
      },
      onRemoteUpdate: () => {
        recordAppActivity();
        lastRemoteUpdateTimeRef.current = Date.now();
      },
      onSessionEnd: () => {
        setRoomActiveCount(0);
      },
      onSaveAckUpdate: (eTag, savedVersion, _requestId, fingerprint) => {
        // Peer received a SAVE_ACK from the leader: sync eTag and, if our
        // current scene is at or behind the saved version, clear dirty state.
        currentETagRef.current = eTag;
        void isCurrentCollabSnapshotSaved(excalidrawAPI, savedVersion, fingerprint).then(
          (fullySaved) => {
            if (collabRef.current !== client) return;
            lastSaveVersionRef.current = Math.max(lastSaveVersionRef.current, savedVersion);
            setIsDirty(!fullySaved);
            setSaveStatus(fullySaved ? "saved" : "unsaved");
          },
          () => {
            if (collabRef.current !== client) return;
            setIsDirty(true);
            setSaveStatus("unsaved");
          },
        );
      },
      onSaveRequest: (requestId, requestedVersion, requestedFingerprint) => {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
        void (async () => {
          try {
            const synchronized = await waitForRequestedSceneVersion(
              excalidrawAPI,
              requestedVersion,
              requestedFingerprint,
            );
            if (collabRef.current === client) {
              if (!synchronized || !(await handleSaveRef.current([requestId]))) {
                client.broadcastSaveFailed(requestId);
              }
            }
          } catch {
            if (collabRef.current === client) client.broadcastSaveFailed(requestId);
          }
        })();
      },
      onLeaderTakeover: (pendingRequestIds) => {
        // We just became the save leader — trigger an immediate save so
        // the file is written before we accumulate more unsaved time.
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
        void handleSaveRef.current(pendingRequestIds);
      },
    });

    collabRef.current = client;

    if (autoJoinFileCollaborationRef.current) {
      void client.connect();
    }

    return () => {
      client.destroy();
      collabRef.current = null;
    };
  }, [
    excalidrawAPI,
    collabAccountIdentity,
    driveId,
    itemId,
    canonicalDrawingId,
    isNewDrawing,
    isEphemeralJoin,
    getToken,
    shareToken,
    recordAppActivity,
  ]);

  // -----------------------------------------------------------------------
  // 3b. Auto-join ephemeral room when navigating to /join/:inviteToken
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!drawingAiSharing.grant || !collabRef.current || autoJoinFileCollaborationRef.current) {
      return;
    }
    autoJoinFileCollaborationRef.current = true;
    void collabRef.current.connect();
  }, [drawingAiSharing.grant, excalidrawAPI, driveId, itemId]);

  useEffect(() => {
    if (
      !aiConnectionCleanupPending ||
      hasBroadCollaborationSharing ||
      hasPeopleSharing ||
      drawingAiSharing.grant ||
      roomActiveCount > 1
    ) {
      return;
    }
    // Revocation may remove verified AI participants on a subsequent room update.
    autoJoinFileCollaborationRef.current = false;
    collabRef.current?.disconnect();
    setIsCollaborating(false);
    setRoomActiveCount(0);
    excalidrawAPI?.updateScene({ collaborators: new Map() });
    setAiConnectionCleanupPending(false);
  }, [
    aiConnectionCleanupPending,
    hasBroadCollaborationSharing,
    hasPeopleSharing,
    drawingAiSharing.grant,
    roomActiveCount,
    excalidrawAPI,
  ]);

  useEffect(() => {
    if (!excalidrawAPI || !account || !isEphemeralJoin || !routeInviteToken) return;

    const client = new CollabClient({
      excalidrawAPI,
      getToken,
      inviteToken: routeInviteToken,
      username: account.name || account.username,
      onRemoteUpdate: recordAppActivity,
      onConnectionChange: (connected) => {
        setIsCollaborating(connected);
        if (!connected) {
          setRoomActiveCount(0);
        }
      },
      onRoomUserCountChange: (count) => {
        setRoomActiveCount(count);
      },
      onSessionRoleResolved: (accessLevel, canCreateLinks) => {
        setEphemeralAccessLevel(accessLevel);
        setIsEphemeralRoomOwner(canCreateLinks);
      },
      onRoomIdAssigned: () => {
        // Already on /join/<token> — no URL change needed
      },
      onSessionEnd: () => {
        collabRef.current = null;
        setIsCollaborating(false);
        setRoomActiveCount(0);
        // Ephemeral room ended — go back to a fresh scene
        navigate(`/?_ts=${Date.now()}`);
      },
    });

    collabRef.current = client;
    void client.connect();

    return () => {
      client.destroy();
      collabRef.current = null;
    };
  }, [
    excalidrawAPI,
    account,
    isEphemeralJoin,
    routeInviteToken,
    getToken,
    navigate,
    recordAppActivity,
  ]);

  useEffect(() => {
    if (!account || !importShareBlobId) return;

    // ⚠️ SECURITY: Never import a shared blob into an active collab session.
    // The imported scene would be broadcast to all collaborators via updateScene
    // → onChange → broadcastUpdate, leaking the blob content to the room.
    // `isSpeDrawing` is derived from the route, so the guard holds from the first
    // render — it must not wait for the drawing's container coordinates to load.
    if (isEphemeralJoin || isSpeDrawing) return;

    let cancelled = false;
    setError(null);

    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error("Not authenticated");

        const shared = await backendClient.loadSharedScene(token, importShareBlobId);
        if (cancelled) return;

        setPendingImportedScene(restoreScene(shared.scene));
        setShowLoadFromLinkDialog(true);
      } catch (err) {
        if (cancelled) return;
        console.error("[EditorPage] Failed to load shared scene:", err);
        setError({ kind: "generic", message: "Failed to load the shared drawing." });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [account, getToken, importShareBlobId, isEphemeralJoin, isSpeDrawing]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------
  // -----------------------------------------------------------------------
  // Auto-save (30s debounce, leader-gated, inhibit-aware)
  // -----------------------------------------------------------------------
  const persistSceneOnlyDraft = useCallback(() => {
    if (
      skipBeforeUnloadRef.current ||
      !canWriteSceneStorage() ||
      !isNewDrawing ||
      isEphemeralJoin ||
      !excalidrawAPI
    ) {
      return;
    }
    writeSceneStorage(sceneOnlyStorageKey(), serializeFromAPI(excalidrawAPI));
  }, [isNewDrawing, isEphemeralJoin, excalidrawAPI]);

  const scheduleSceneOnlyDraftSave = useCallback(() => {
    if (skipBeforeUnloadRef.current || !canWriteSceneStorage()) return;
    if (sceneOnlySaveTimerRef.current) clearTimeout(sceneOnlySaveTimerRef.current);
    sceneOnlySaveTimerRef.current = setTimeout(() => {
      sceneOnlySaveTimerRef.current = null;
      persistSceneOnlyDraft();
    }, LOCAL_SCENE_SAVE_DELAY);
  }, [persistSceneOnlyDraft]);

  const quiesceSceneWriters = useCallback(() => {
    skipBeforeUnloadRef.current = true;
    if (sceneOnlySaveTimerRef.current) {
      clearTimeout(sceneOnlySaveTimerRef.current);
      sceneOnlySaveTimerRef.current = null;
    }
  }, []);

  const resumeSceneWriters = useCallback(() => {
    skipBeforeUnloadRef.current = false;
    if (isNewDrawing && !isEphemeralJoin) {
      scheduleSceneOnlyDraftSave();
    }
  }, [isEphemeralJoin, isNewDrawing, scheduleSceneOnlyDraftSave]);

  useEffect(
    () =>
      subscribeToSceneWriterInvalidation((invalidated) => {
        if (invalidated) {
          quiesceSceneWriters();
          clearStagedImportedScene();
          setRecoveryBackup(null);
        } else {
          resumeSceneWriters();
        }
      }),
    [quiesceSceneWriters, resumeSceneWriters],
  );

  const handleSignOut = useCallback(async () => {
    quiesceSceneWriters();
    clearGalleryCache();
    try {
      await doSignOut();
    } catch {
      if (canWriteSceneStorage()) {
        resumeSceneWriters();
      }
    }
  }, [doSignOut, quiesceSceneWriters, resumeSceneWriters]);

  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;

      // Leader gate: only auto-save if we're the leader or not collaborating
      const collab = collabRef.current;
      if (collab && collab.getIsConnected() && !collab.getIsLeader()) return;

      // Save-inhibit: skip if remote update was recent (unless stale)
      if (
        shouldDeferAutosave({
          now: Date.now(),
          lastRemoteChangeAt: lastRemoteUpdateTimeRef.current,
          lastSavedAt: lastSaveTimeRef.current,
        })
      ) {
        // Reschedule
        scheduleAutoSave();
        return;
      }

      handleSaveRef.current();
    }, COLLAB_AUTOSAVE_DELAY_MS);
  }, []);

  // Cleanup auto-save timers
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (sceneOnlySaveTimerRef.current) clearTimeout(sceneOnlySaveTimerRef.current);
    };
  }, []);

  // The scene-only browser slot is NOT cleared when entering file-backed mode.
  // It is the user's own drawing, and stop-session case B (collaboration-
  // protocol § 3.4) returns them to it. It is cleared only when promoted to a
  // cloud drawing or when the user explicitly navigates away from it.

  const handleChange = useCallback(
    (elements: readonly ExcalidrawElement[], appState: AppState) => {
      const isLocalSceneMutation = collabRef.current?.broadcastUpdate(elements) ?? true;

      // When a native Excalidraw dialog closes, bring LoadFromLinkDialog back to front
      if (!appState.openDialog && nativeDialogActiveRef.current) {
        nativeDialogActiveRef.current = false;
        setNativeDialogActive(false);
      }

      // Dirty tracking — only from local mutations (skip remote updates via version guard in CollabClient)
      const currentVersion = sceneVersion(elements as ExcalidrawElement[]);

      // After file load, Excalidraw's internal restore may bump element versions
      // beyond what our restoreScene() computed. Absorb the first onChange to sync
      // the baseline version, preventing a false "unsaved" indicator.
      if (suppressDirtyUntilSettledRef.current) {
        suppressDirtyUntilSettledRef.current = false;
        lastSaveVersionRef.current = currentVersion;
        return;
      }

      if (
        isLocalSceneMutation &&
        currentVersion !== lastSaveVersionRef.current &&
        !editorEngagedTelemetrySentRef.current
      ) {
        editorEngagedTelemetrySentRef.current = true;
        void getToken()
          .then((token) =>
            backendClient.postClientTelemetry(token, [
              {
                eventName: "editor_engaged",
                eventId: crypto.randomUUID(),
                occurredAt: new Date().toISOString(),
              },
            ]),
          )
          .catch(() => undefined);
      }

      if (
        isNewDrawing &&
        !isEphemeralJoin &&
        !drawingStartedTelemetrySentRef.current &&
        elements.some((element) => !element.isDeleted)
      ) {
        drawingStartedTelemetrySentRef.current = true;
        void getToken()
          .then((token) =>
            backendClient.postClientTelemetry(token, [
              {
                eventName: "drawing_started",
                eventId: crypto.randomUUID(),
                occurredAt: new Date().toISOString(),
              },
            ]),
          )
          .catch(() => undefined);
      }

      if (currentVersion !== lastSaveVersionRef.current) {
        setIsDirty(true);
        if (!saveInFlightRef.current) {
          setSaveStatus("unsaved");
        }
        if (isNewDrawing) {
          scheduleSceneOnlyDraftSave();
        } else {
          scheduleAutoSave();
        }
      }
    },
    [getToken, isEphemeralJoin, isNewDrawing, scheduleAutoSave, scheduleSceneOnlyDraftSave],
  );

  const handlePointerUpdate = useCallback(
    (payload: { pointer: { x: number; y: number; tool: string }; button: string }) => {
      collabRef.current?.broadcastCursor(payload);
    },
    [],
  );

  // -----------------------------------------------------------------------
  // Save handler (eTag-aware, conflict handling)
  // -----------------------------------------------------------------------
  const handleSave = useCallback(
    async (requestIds: string[] = []): Promise<boolean> => {
      const activeSave = localSavePromiseRef.current;
      if (activeSave) {
        const saved = await activeSave;
        return requestIds.length > 0 ? handleSaveRef.current(requestIds) : saved;
      }
      if (!excalidrawAPI) return false;
      // Scene-Only mode never auto-creates storage. The explicit Save drawing
      // action performs the SPE transition.
      if (isNewDrawing) return false;
      // Leader gate: non-leaders should not save during active collaboration.
      // The collab leader's auto-save captures the full reconciled scene for everyone.
      const collab = collabRef.current;
      const collaborationWasConnected = collab?.getIsConnected();
      if (collab && autoJoinFileCollaborationRef.current && !collaborationWasConnected) {
        setSaveStatus("offline");
        return false;
      }
      if (collab && collab.getIsConnected() && !collab.getIsLeader()) return false;

      if (!isSpeDrawing) return false;
      for (const requestId of requestIds) {
        pendingLeaderSaveRequestIdsRef.current.add(requestId);
      }
      let resolveLocalSave!: (saved: boolean) => void;
      localSavePromiseRef.current = new Promise<boolean>((resolve) => {
        resolveLocalSave = resolve;
      });
      saveInFlightRef.current = true;
      setIsSaving(true);
      setSaveStatus("saving");
      let saved = false;
      let savedSceneVersion = 0;
      let savedSceneFingerprint: string | undefined;
      let savedElementsJson = "";
      const applySavedVersion = () => {
        lastSaveVersionRef.current = savedSceneVersion;
        lastSaveTimeRef.current = Date.now();
        const currentVersion = currentSceneVersion(excalidrawAPI);
        const fullySaved =
          currentVersion <= savedSceneVersion &&
          canonicalSceneJson(excalidrawAPI.getSceneElementsIncludingDeleted()) ===
            savedElementsJson;
        setIsDirty(!fullySaved);
        setSaveStatus(fullySaved ? "saved" : "unsaved");
      };
      try {
        const token = await getToken();
        const assertSaveAuthority = () => {
          if (
            (collaborationWasConnected &&
              (collabRef.current !== collab ||
                !collab?.getIsConnected() ||
                !collab.getIsLeader())) ||
            (!collaborationWasConnected &&
              (collabRef.current?.getIsConnected() || autoJoinFileCollaborationRef.current))
          ) {
            throw new Error("Collaboration save leadership changed");
          }
        };
        for (let attempt = 0; attempt < 2; attempt++) {
          assertSaveAuthority();
          let eTag = currentETagRef.current;
          if (collaborationWasConnected && collab) {
            const latest = await backendClient.loadDrawing(token, canonicalDrawingId!);
            assertSaveAuthority();
            collab.reconcileCloudScene(latest.scene);
            eTag = latest.eTag;
            currentETagRef.current = eTag;
          }
          const snapshot = captureCollabSnapshot(excalidrawAPI);
          savedSceneVersion = snapshot.version;
          const json = snapshot.sceneJson;
          savedElementsJson = canonicalSceneJson(snapshot.elements);
          savedSceneFingerprint = await fingerprintCollabElements(snapshot.elements);
          if (!eTag) {
            throw new Error("Drawing ETag is unavailable");
          }
          const previewPngBase64 = await createDrawingPreview(excalidrawAPI);
          assertSaveAuthority();
          try {
            const result = await backendClient.saveDrawing(
              token,
              canonicalDrawingId!,
              {
                sceneJson: json,
                previewPngBase64,
              },
              eTag,
              undefined,
              collab?.getParticipantId() ?? undefined,
            );
            cachePreviewFromBase64(canonicalDrawingId!, result.updatedAt, previewPngBase64);
            currentETagRef.current = result.eTag;
            applySavedVersion();
            if (driveId && itemId) localStorage.removeItem(recoveryStorageKey(driveId, itemId));
            saved = true;
            break;
          } catch (error) {
            const failure = error as { status?: number; code?: string };
            if (
              attempt === 0 &&
              collaborationWasConnected &&
              failure.status === 409 &&
              !failure.code
            ) {
              assertSaveAuthority();
              continue;
            }
            throw error;
          }
        }
      } catch (err: unknown) {
        const httpErr = err as { status?: number; message?: string; code?: string };
        const coordinationFailure =
          httpErr.code === "MIGRATION_REQUIRED" || httpErr.code?.startsWith("COLLAB_");
        const collaborativeSave =
          collaborationWasConnected ||
          collabRef.current?.getIsConnected() ||
          autoJoinFileCollaborationRef.current;
        if (httpErr.status === 409 && !collaborativeSave && !coordinationFailure) {
          setSaveStatus("conflict");
          setSaveConflictError(null);
          setSaveConflictOpen(true);
          return false;
        } else {
          console.error("[EditorPage] Save failed:", err);
          setSaveStatus(shouldTreatAsOffline(err) ? "offline" : "error");
        }
      } finally {
        const pendingRequestIds = Array.from(pendingLeaderSaveRequestIdsRef.current);
        pendingLeaderSaveRequestIdsRef.current.clear();
        const activeCollab = collabRef.current;
        if (saved && currentETagRef.current) {
          if (pendingRequestIds.length > 0) {
            for (const requestId of pendingRequestIds) {
              activeCollab?.broadcastSaveAck(
                currentETagRef.current,
                savedSceneVersion,
                requestId,
                savedSceneFingerprint,
              );
            }
          } else {
            activeCollab?.broadcastSaveAck(
              currentETagRef.current,
              savedSceneVersion,
              undefined,
              savedSceneFingerprint,
            );
          }
        } else {
          for (const requestId of pendingRequestIds) {
            activeCollab?.broadcastSaveFailed(requestId);
          }
        }
        localSavePromiseRef.current = null;
        if (!delegatedSavePromiseRef.current) {
          saveInFlightRef.current = false;
        }
        setIsSaving(false);
        resolveLocalSave(saved);
      }
      return saved;
    },
    [excalidrawAPI, driveId, itemId, isNewDrawing, getToken, isSpeDrawing, canonicalDrawingId],
  );

  // Keep ref in sync so scheduleAutoSave (stable closure) always calls the latest handleSave
  handleSaveRef.current = handleSave;

  const handleOverwriteSaveConflict = useCallback(async () => {
    if (!excalidrawAPI || isNewDrawing || drawingRole === "viewer") return;
    if (!isSpeDrawing || !canonicalDrawingId) return;
    if (collabRef.current?.getIsConnected() || autoJoinFileCollaborationRef.current) {
      setSaveConflictOpen(false);
      await handleSaveRef.current();
      return;
    }
    setSaveConflictBusy(true);
    setSaveConflictError(null);
    setIsSaving(true);
    setSaveStatus("saving");
    try {
      const token = await getToken();
      const snapshot = captureCollabSnapshot(excalidrawAPI);
      const savedVersion = snapshot.version;
      const json = snapshot.sceneJson;
      const savedFingerprint = await fingerprintCollabElements(snapshot.elements);
      const latest = await backendClient.loadDrawing(token, canonicalDrawingId);
      const previewPngBase64 = await createDrawingPreview(excalidrawAPI);
      const collab = collabRef.current;
      if (
        collab &&
        autoJoinFileCollaborationRef.current &&
        (!collab.getIsConnected() || !collab.getIsLeader())
      ) {
        throw new Error("The current collaboration leader must coordinate the save");
      }
      const result = await backendClient.saveDrawing(
        token,
        canonicalDrawingId,
        {
          sceneJson: json,
          previewPngBase64,
        },
        latest.eTag,
        undefined,
        collab?.getParticipantId() ?? undefined,
      );
      cachePreviewFromBase64(canonicalDrawingId, result.updatedAt, previewPngBase64);

      currentETagRef.current = result.eTag;
      lastSaveVersionRef.current = savedVersion;
      lastSaveTimeRef.current = Date.now();
      const fullySaved = await isCurrentCollabSnapshotSaved(
        excalidrawAPI,
        savedVersion,
        savedFingerprint,
      );
      setIsDirty(!fullySaved);
      setSaveStatus(fullySaved ? "saved" : "unsaved");
      if (driveId && itemId) {
        localStorage.removeItem(recoveryStorageKey(driveId, itemId));
      }
      if (result.eTag) {
        collabRef.current?.broadcastSaveAck(result.eTag, savedVersion, undefined, savedFingerprint);
      }
      setSaveConflictOpen(false);
    } catch (error) {
      const httpError = error as { status?: number };
      setSaveStatus("conflict");
      setSaveConflictError(
        httpError.status === 409
          ? "The drawing changed again before the overwrite completed. Review the latest version or try overwriting again."
          : "The cloud version could not be overwritten. Please try again.",
      );
    } finally {
      setIsSaving(false);
      setSaveConflictBusy(false);
    }
  }, [
    canonicalDrawingId,
    drawingRole,
    driveId,
    excalidrawAPI,
    getToken,
    isNewDrawing,
    isSpeDrawing,
    itemId,
  ]);

  const requestCollaborativeSave = useCallback(async (): Promise<boolean> => {
    if (!excalidrawAPI) return false;
    if (delegatedSavePromiseRef.current) return delegatedSavePromiseRef.current;
    const collab = collabRef.current;
    if (!collab?.getIsConnected() || collab.getIsLeader()) {
      return handleSave();
    }

    const operation = (async () => {
      saveInFlightRef.current = true;
      setIsSaving(true);
      setSaveStatus("saving");
      try {
        collab.flushPendingUpdates();
        const requestedVersion = currentSceneVersion(excalidrawAPI);
        const requestedFingerprint = await fingerprintCollabElements(
          excalidrawAPI.getSceneElementsIncludingDeleted(),
        );
        const acknowledgement = await collab.requestSaveFromLeader(
          requestedVersion,
          requestedFingerprint,
        );
        return isCurrentCollabSnapshotSaved(
          excalidrawAPI,
          acknowledgement.sceneVersion,
          acknowledgement.sceneFingerprint,
        );
      } catch (error) {
        console.error("[EditorPage] Collaborative save failed:", error);
        setSaveStatus("error");
        return false;
      } finally {
        if (!localSavePromiseRef.current) {
          saveInFlightRef.current = false;
        }
        setIsSaving(false);
      }
    })();

    delegatedSavePromiseRef.current = operation;
    try {
      return await operation;
    } finally {
      delegatedSavePromiseRef.current = null;
    }
  }, [excalidrawAPI, handleSave]);

  const handleManualSave = useCallback(() => {
    if (isNewDrawing || drawingRole === "viewer") return;
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    void requestCollaborativeSave();
  }, [drawingRole, isNewDrawing, requestCollaborativeSave]);

  const handleStartSession = useCallback(async () => {
    const collab = collabRef.current;

    // Scene-only mode — no existing client, create an ephemeral room
    if (!collab && isNewDrawing && excalidrawAPI && account) {
      const client = new CollabClient({
        excalidrawAPI,
        getToken,
        username: account.name || account.username,
        onRemoteUpdate: recordAppActivity,
        onConnectionChange: (connected) => {
          setIsCollaborating(connected);
          if (!connected) {
            setRoomActiveCount(0);
          }
        },
        onRoomUserCountChange: (count) => {
          setRoomActiveCount(count);
        },
        onSessionRoleResolved: (accessLevel, canCreateLinks) => {
          setEphemeralAccessLevel(accessLevel);
          setIsEphemeralRoomOwner(canCreateLinks);
        },
        onRoomIdAssigned: (_roomId, selfInviteToken) => {
          // Update URL so it's shareable — use replaceState to avoid
          // React Router re-keying which would unmount and destroy the
          // CollabClient we just connected.
          window.history.replaceState(null, "", `/join/${selfInviteToken}`);
        },
        onSessionEnd: () => {
          collabRef.current = null;
          setIsCollaborating(false);
          setRoomActiveCount(0);
          // Revert URL without remount — preserves the user's local scene
          window.history.replaceState(null, "", "/");
        },
      });
      collabRef.current = client;
      await client.connect();
      return;
    }

    if (!collab) return;
    if (collab.getIsConnected()) return;
    if (isFileBacked) autoJoinFileCollaborationRef.current = true;
    await collab.connect();
  }, [isNewDrawing, isFileBacked, excalidrawAPI, account, getToken, recordAppActivity]);

  // ---------------------------------------------------------------------
  // Stop session — see collaboration-protocol.md § 3.4
  // ---------------------------------------------------------------------

  /**
   * Which stop-session outcome applies — see resolveStopSessionCase and
   * collaboration-protocol.md § 3.4.
   */
  const resolveStopCase = useCallback(
    (): StopSessionCase =>
      resolveStopSessionCase({
        isFileBacked,
        isRoomUrlJoin: isEphemeralJoin,
        browserSlotHasDrawing: hasLocalSceneDraft(),
      }),
    [isFileBacked, isEphemeralJoin],
  );

  const handleStopSession = useCallback(
    async (choice: StopSessionChoice = "default") => {
      const collab = collabRef.current;
      if (!collab) return;

      const seedLocalSlot = () => {
        if (skipBeforeUnloadRef.current || !canWriteSceneStorage() || !excalidrawAPI) return;
        writeSceneStorage(sceneOnlyStorageKey(), serializeFromAPI(excalidrawAPI));
      };

      const teardown = () => {
        collab.disconnect();
        collab.destroy();
        collabRef.current = null;
        setIsCollaborating(false);
        setRoomActiveCount(0);
      };

      if (collab.getIsEphemeral()) {
        // Started from a local scene: scene-only autosave has kept the slot in
        // step with the room all session, so the URL reverts in place and the
        // live scene is preserved without a remount.
        if (!isEphemeralJoin) {
          teardown();
          window.history.replaceState(null, "", "/");
          return;
        }

        // Joined by room URL: autosave was suppressed to keep private local
        // work out of the room, so the slot is stale. Remounting at "/" is what
        // re-enables autosave — without it nothing drawn afterwards persists.
        if (choice !== "keep-browser") seedLocalSlot();
        teardown();
        navigate(`/?_ts=${Date.now()}`);
        return;
      }

      // File-backed: move to Scene-Only. Seed the slot with this drawing only
      // when it is empty — an existing local drawing is real unsaved work and
      // must never be overwritten. The cloud drawing is already saved and
      // reachable from the gallery either way.
      if (!hasLocalSceneDraft()) seedLocalSlot();
      teardown();
      navigate(`/?_ts=${Date.now()}`);
    },
    [excalidrawAPI, isEphemeralJoin, navigate],
  );

  // -----------------------------------------------------------------------
  // Navigate away — tear down collab, cancel auto-save, navigate to fresh scene
  // -----------------------------------------------------------------------
  const performNavigateAway = useCallback(() => {
    // Tear down collab connection if active
    const collab = collabRef.current;
    if (collab) {
      collab.destroy();
      collabRef.current = null;
    }
    // Cancel pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (sceneOnlySaveTimerRef.current) {
      clearTimeout(sceneOnlySaveTimerRef.current);
      sceneOnlySaveTimerRef.current = null;
    }
    localStorage.removeItem(sceneOnlyStorageKey());
    // Navigate to / with a unique timestamp so EditorPageKeyed remounts even
    // when already on a scene-only route (key changes from "new-<old>" to "new-<new>").
    navigate(`/?_ts=${Date.now()}`);
  }, [navigate]);

  // Unsaved-changes guard state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [unsavedSaving, setUnsavedSaving] = useState(false);

  // Guarded navigation — a remaining editor owns persistence handoff, while the
  // last editor must explicitly resolve unsaved work before leaving.
  const guardedNavigateAway = useCallback(() => {
    if (!isDirty) {
      performNavigateAway();
      return;
    }

    const collab = collabRef.current;
    if (isFileBacked && collab?.getIsConnected() && collab.hasOtherSaveCapableEditor()) {
      collab.flushPendingUpdates();
      void (async () => {
        await requestCollaborativeSave();
        performNavigateAway();
      })();
      return;
    }

    setShowUnsavedDialog(true);
  }, [isDirty, isFileBacked, performNavigateAway, requestCollaborativeSave]);

  // Keep ref in sync so renderTopRightUI (which Excalidraw may memoize) always
  // calls the latest guardedNavigateAway with the current isDirty value.
  guardedNavigateAwayRef.current = guardedNavigateAway;

  const handleUnsavedAction = useCallback(
    async (action: UnsavedAction) => {
      if (action === "cancel") {
        // Bump token so any in-flight save knows it was cancelled
        saveAttemptRef.current += 1;
        setShowUnsavedDialog(false);
        setUnsavedSaving(false);
        return;
      }
      if (action === "discard") {
        // Bump token so any in-flight save knows it was superseded
        saveAttemptRef.current += 1;
        setShowUnsavedDialog(false);
        setUnsavedSaving(false);
        performNavigateAway();
        return;
      }
      // action === "save"
      setUnsavedSaving(true);
      const token = ++saveAttemptRef.current;
      const saved = await requestCollaborativeSave();
      // If another action (cancel/discard) happened while we were saving,
      // this save attempt is stale — don't navigate.
      if (token !== saveAttemptRef.current) return;
      setUnsavedSaving(false);
      if (!saved) {
        // Save failed — stay on page so user doesn't lose work
        return;
      }
      setShowUnsavedDialog(false);
      performNavigateAway();
    },
    [performNavigateAway, requestCollaborativeSave],
  );

  /** Build the scene-only room link for a role. Only the room starter holds these. */
  const ephemeralShareUrlForRole = useCallback((role: DrawingAccessRole): string | null => {
    const collab = collabRef.current;
    if (!collab?.getIsEphemeral()) return null;
    const inviteToken = collab.getInviteToken(role === "viewer" ? "viewer" : "editor");
    if (!inviteToken) return null;
    return `${window.location.origin}/join/${inviteToken}`;
  }, []);

  const resolveCloudShareUrl = useCallback(
    async (role: DrawingAccessRole) => {
      if (!canonicalDrawingId) {
        throw new Error("This legacy file must be migrated before it can be shared");
      }
      if (drawingRole !== "owner") {
        throw new Error("Only the drawing owner can create a sharing link");
      }

      // Reuse the link already resolved for this role so switching between
      // "Can edit" and "Can view" never mints a second URL or restarts the
      // expiry window for a role the owner already has a link for.
      const urlCache = shareUrlByRoleRef.current;
      const pendingCache = shareUrlPromiseByRoleRef.current;
      const cacheGeneration = shareCacheGenerationRef.current;
      const cached = urlCache.get(role);
      if (cached) return cached;

      const pending = pendingCache.get(role);
      if (pending) return pending;

      const resolution = (async () => {
        const token = await getToken();
        if (!token) throw new Error("Not authenticated");
        const result = await backendClient.createDrawingShare(token, canonicalDrawingId, role);
        if (shareCacheGenerationRef.current === cacheGeneration) {
          setHasPeopleSharing(true);
          setPeopleSharingKind("link");
        }
        urlCache.set(role, result.shareUrl);
        return result.shareUrl;
      })();
      pendingCache.set(role, resolution);
      try {
        return await resolution;
      } finally {
        if (pendingCache.get(role) === resolution) {
          pendingCache.delete(role);
        }
      }
    },
    [canonicalDrawingId, drawingRole, getToken],
  );

  const lookupCloudShareUrl = useCallback(
    async (role: DrawingAccessRole): Promise<string | null> => {
      if (!canonicalDrawingId || drawingRole !== "owner") return null;
      const urlCache = shareUrlByRoleRef.current;
      const cacheGeneration = shareCacheGenerationRef.current;
      const cached = urlCache.get(role);
      if (cached) return cached;

      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const share = (await backendClient.lookupDrawingShare(token, canonicalDrawingId, role)).share;
      if (!share) return null;
      if (shareCacheGenerationRef.current === cacheGeneration) {
        urlCache.set(share.role, share.shareUrl);
      }
      return share.shareUrl;
    },
    [canonicalDrawingId, drawingRole, getToken],
  );

  const resolveShareUrl = useCallback(async () => {
    // Ephemeral mode — share a signed room invite carrying the chosen role
    const collab = collabRef.current;
    if (collab?.getIsEphemeral()) {
      const url = ephemeralShareUrlForRole(linkRole);
      if (!url) throw new Error("Ephemeral room not ready");
      return url;
    }

    // Canonical SPE mode — the backend creates the Graph permission but only
    // exposes the self-contained Excalidraw-domain redemption URL.
    return resolveCloudShareUrl(linkRole);
  }, [ephemeralShareUrlForRole, linkRole, resolveCloudShareUrl]);

  const handleLinkRoleChange = useCallback(
    (role: DrawingAccessRole) => {
      const previousRole = linkRole;
      setLinkError(null);
      setLinkRole(role);

      // Ephemeral rooms already hold both invite tokens from connect time, so
      // switching roles is a local swap with nothing to fetch.
      if (collabRef.current?.getIsEphemeral()) {
        setActiveShareUrl(ephemeralShareUrlForRole(role));
        return;
      }

      if (!canonicalDrawingId || drawingRole !== "owner") return;
      const requestId = ++linkResolutionRequestRef.current;
      setLinkRoleBusy(true);
      void (async () => {
        try {
          // Selecting a role while a link is shown is explicit consent for that
          // role, so create its link in place when none exists yet.
          const url = (await lookupCloudShareUrl(role)) ?? (await resolveCloudShareUrl(role));
          if (linkResolutionRequestRef.current === requestId) {
            setActiveShareUrl(url);
          }
        } catch (err) {
          console.error("[EditorPage] Failed to resolve share URL for role:", err);
          if (linkResolutionRequestRef.current === requestId) {
            setLinkRole(previousRole);
            setLinkError("The sharing link could not be prepared. Select the permission to retry.");
          }
        } finally {
          if (linkResolutionRequestRef.current === requestId) {
            setLinkRoleBusy(false);
          }
        }
      })();
    },
    [
      canonicalDrawingId,
      drawingRole,
      ephemeralShareUrlForRole,
      lookupCloudShareUrl,
      resolveCloudShareUrl,
      linkRole,
    ],
  );

  const handleShareLink = useCallback(async () => {
    const shareUrl = await resolveShareUrl();
    await copyLink(shareUrl, isFileBacked ? currentFileNameRef.current : undefined, linkRole);
  }, [isFileBacked, linkRole, resolveShareUrl]);

  const handleCopyInvalidSceneAiRepairPrompt = useCallback(async () => {
    const diagnostic = error?.diagnostic;
    if (!diagnostic) return;

    const prompt = `Help me repair a drawing in Excalidraw-MS using MCP.

Use the Excalidraw-MS MCP tools to inspect the stored drawing, correct only the invalid scene data, validate the complete scene, and save it without deleting or replacing valid content. If you do not have access to this drawing, ask me to share it with AI first.

Technical details:
${diagnostic}`;

    setCopyDetailsError(null);
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API not available");
      }
      await navigator.clipboard.writeText(prompt);
    } catch (clipboardError) {
      console.error("[EditorPage] Failed to copy AI repair prompt:", clipboardError);
      setCopyDetailsError("Couldn't copy the AI repair prompt. Please try again.");
    }
  }, [error?.diagnostic]);

  const handleExportToLink = useCallback(
    async (
      elements: readonly ExcalidrawElement[],
      appState: Partial<AppState>,
      files: BinaryFiles,
    ) => {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      const result = await backendClient.exportToLink(
        token,
        serializeScene(elements, appState, files),
      );
      setShareableLinkUrl(`${window.location.origin}${result.viewUrl}`);
    },
    [getToken],
  );

  const openImageExportDialog = useCallback(() => {
    if (!excalidrawAPI) return;
    nativeDialogActiveRef.current = true;
    setNativeDialogActive(true);
    excalidrawAPI.updateScene({ appState: { openDialog: { name: "imageExport" } } });
  }, [excalidrawAPI]);

  const saveToDisk = useCallback(async () => {
    if (!excalidrawAPI) return;
    const json = serializeFromAPI(excalidrawAPI);
    const blob = new Blob([json], { type: "application/json" });
    if ("showSaveFilePicker" in window) {
      try {
        const handle = await (
          window as Window & { showSaveFilePicker: (opts: object) => Promise<FileSystemFileHandle> }
        ).showSaveFilePicker({
          suggestedName: "drawing.excalidraw",
          types: [
            { description: "Excalidraw file", accept: { "application/json": [".excalidraw"] } },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        // User cancelled the picker — ignore
        if (err instanceof Error && err.name !== "AbortError") throw err;
      }
    } else {
      // Fallback for browsers without File System Access API
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "drawing.excalidraw";
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [excalidrawAPI]);

  const openSaveToCloudDialog = useCallback(() => {
    excalidrawAPI?.updateScene({ appState: { openDialog: null } });
    setShowSaveToCloudDialog(true);
  }, [excalidrawAPI]);

  // Resolved links belong to one drawing. Drop them when the drawing changes so
  // a stale link is never shown for a different file.
  useEffect(() => {
    linkResolutionRequestRef.current += 1;
    shareCacheGenerationRef.current += 1;
    shareUrlByRoleRef.current = new Map();
    shareUrlPromiseByRoleRef.current = new Map();
    setActiveShareUrl(null);
    setLinkRole("editor");
    setLinkRoleBusy(false);
    setLinkError(null);
    setPeopleAccessError(null);
    setPeopleAccessRetryLabel("Retry people access");
    peoplePreparationRetryRef.current = null;
  }, [canonicalDrawingId]);

  const handleOpenCollabShareDialog = useCallback(async () => {
    setShareableLinkUrl(null);
    if (isFileBacked && canCreateLiveShareLink) {
      linkResolutionRequestRef.current += 1;
      shareCacheGenerationRef.current += 1;
      shareUrlByRoleRef.current = new Map();
      shareUrlPromiseByRoleRef.current = new Map();
      setActiveShareUrl(null);
      setLinkRole("editor");
      setLinkRoleBusy(false);
      setLinkError(null);
      setHasPeopleSharing(undefined);
      setPeopleSharingKind(undefined);
      setPeopleAccessLoading(true);
      setPeopleAccessError(null);
      setPeopleAccessRetryLabel("Retry people access");
      peoplePreparationRetryRef.current = null;
    }
    // Snapshot at open time so the stop-session confirmation can describe where
    // the user will actually land after leaving this session.
    setStopCase(resolveStopCase());
    setShowCollabShareDialog(true);
  }, [canCreateLiveShareLink, isFileBacked, resolveStopCase]);

  // Recheck access on every open; invitation expiry and live presence are not
  // sharing status. Keep this request independent of connection-state changes.
  useEffect(() => {
    if (!showCollabShareDialog || !canCreateLiveShareLink || !isFileBacked) return;
    if (sceneLoading || !canonicalDrawingId) return;

    let cancelled = false;
    const requestId = ++linkResolutionRequestRef.current;
    setPeopleAccessLoading(true);
    setPeopleAccessError(null);
    setLinkError(null);
    void (async () => {
      try {
        const token = await getToken();
        const sharing = await readPeopleSharing(token, canonicalDrawingId, (isShared, kind) => {
          if (cancelled || linkResolutionRequestRef.current !== requestId) return false;
          setHasPeopleSharing(isShared);
          setPeopleSharingKind(kind);
          return true;
        });
        if (!cancelled && linkResolutionRequestRef.current === requestId) {
          shareUrlByRoleRef.current = new Map(
            sharing.links.map((share) => [share.role, share.shareUrl]),
          );
          const share = sharing.links.find((link) => link.role === "editor") ?? sharing.links[0];
          setActiveShareUrl(share?.shareUrl ?? null);
          setLinkRole(share?.role ?? "editor");
        }
      } catch (err) {
        console.error("[EditorPage] Failed to read people access:", err);
        if (!cancelled && linkResolutionRequestRef.current === requestId) {
          if (err instanceof PeopleLinkPreparationError) {
            peoplePreparationRetryRef.current = err.retry;
            setPeopleAccessRetryLabel("Retry link preparation");
            setPeopleAccessError(
              "A viewer invitation link could not be prepared. Please try again.",
            );
          } else {
            setPeopleAccessError("People sharing details could not be loaded. Please try again.");
          }
        }
      } finally {
        if (!cancelled && linkResolutionRequestRef.current === requestId)
          setPeopleAccessLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (linkResolutionRequestRef.current === requestId) {
        linkResolutionRequestRef.current += 1;
      }
      setPeopleAccessLoading(false);
    };
  }, [
    canCreateLiveShareLink,
    showCollabShareDialog,
    isFileBacked,
    sceneLoading,
    canonicalDrawingId,
    getToken,
    peopleAccessRevision,
  ]);

  const retryPeopleAccess = useCallback(() => {
    const retryPreparation = peoplePreparationRetryRef.current;
    if (!retryPreparation) {
      setPeopleAccessRevision((current) => current + 1);
      return;
    }
    const requestId = ++linkResolutionRequestRef.current;
    setPeopleAccessLoading(true);
    setPeopleAccessError(null);
    void retryPreparation()
      .then((sharing) => {
        if (linkResolutionRequestRef.current !== requestId) return;
        shareUrlByRoleRef.current = new Map(
          sharing.links.map((share) => [share.role, share.shareUrl]),
        );
        const share = sharing.links.find((link) => link.role === "editor") ?? sharing.links[0];
        setActiveShareUrl(share?.shareUrl ?? null);
        setLinkRole(share?.role ?? "editor");
        peoplePreparationRetryRef.current = null;
        setPeopleAccessRetryLabel("Retry people access");
      })
      .catch((error: unknown) => {
        console.error("[EditorPage] Failed to prepare people invitation:", error);
        if (linkResolutionRequestRef.current === requestId) {
          setPeopleAccessError("A viewer invitation link could not be prepared. Please try again.");
        }
      })
      .finally(() => {
        if (linkResolutionRequestRef.current === requestId) setPeopleAccessLoading(false);
      });
  }, []);

  useEffect(() => {
    if (showCollabShareDialog && !isFileBacked && canCreateLiveShareLink && isCollaborating) {
      setActiveShareUrl(ephemeralShareUrlForRole(linkRole));
    }
    // A room link dies with its session; never keep showing it after leaving.
    if (!isFileBacked && !isCollaborating) setActiveShareUrl(null);
  }, [
    showCollabShareDialog,
    isFileBacked,
    canCreateLiveShareLink,
    isCollaborating,
    linkRole,
    ephemeralShareUrlForRole,
  ]);

  const handleStartSessionFromDialog = useCallback(async () => {
    if (isFileBacked && canCreateLiveShareLink) {
      const requestId = ++linkResolutionRequestRef.current;
      setLinkRoleBusy(true);
      try {
        const url = await resolveShareUrl();
        if (linkResolutionRequestRef.current !== requestId) return;
        setActiveShareUrl(url);
      } finally {
        if (linkResolutionRequestRef.current === requestId) setLinkRoleBusy(false);
      }
    }
    await handleStartSession();
  }, [handleStartSession, isFileBacked, canCreateLiveShareLink, resolveShareUrl]);

  const handleRevokeDrawingAi = useCallback(async () => {
    if (drawingAiRevoking) return;
    setDrawingAiRevoking(true);
    setDrawingAiRevokeError(null);
    try {
      await drawingAiSharing.revoke();
      setShowDrawingAiRevoke(false);
      setShowCollabShareDialog(false);
      // This is connection cleanup, not Leave: do not seed a local drawing or navigate.
      if (!hasPeopleSharing) setAiConnectionCleanupPending(true);
    } catch {
      setDrawingAiRevokeError("AI access could not be stopped. Please try again.");
    } finally {
      setDrawingAiRevoking(false);
    }
  }, [drawingAiRevoking, drawingAiSharing, hasPeopleSharing]);

  useEffect(() => {
    if (drawingAiSharing.externalRevocationCount <= observedExternalAiRevocation.current) {
      return;
    }
    observedExternalAiRevocation.current = drawingAiSharing.externalRevocationCount;
    if (!hasPeopleSharing) setAiConnectionCleanupPending(true);
  }, [drawingAiSharing.externalRevocationCount, hasPeopleSharing]);

  const handleCopyActiveLink = useCallback(async () => {
    if (isCloudRecipient) {
      if (!drawingReference.referenceUrl) throw new Error("Drawing link is not ready");
      await copyLink(drawingReference.referenceUrl, currentFileNameRef.current);
      return;
    }
    await handleShareLink();
    setActiveShareUrl(await resolveShareUrl());
  }, [isCloudRecipient, drawingReference.referenceUrl, handleShareLink, resolveShareUrl]);

  const dismissImportShareDialog = useCallback(() => {
    setShowLoadFromLinkDialog(false);
    setPendingImportedScene(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("importShare");
    window.history.replaceState(null, "", url.pathname + url.search);
  }, []);

  const getPreferenceState = useCallback((): SupportedPreferenceState => {
    const appState = excalidrawAPI?.getAppState();
    return {
      toolLock: appState?.activeTool.locked ?? false,
      objectsSnapMode: appState?.objectsSnapModeEnabled ?? false,
      gridMode: appState?.gridModeEnabled ?? false,
      zenMode: appState?.zenModeEnabled ?? false,
      viewMode: isViewModeEnforced || (appState?.viewModeEnabled ?? false),
      canvasProperties: appState?.stats.open ?? false,
    };
  }, [excalidrawAPI, isViewModeEnforced]);

  const handleTogglePreference = useCallback(
    (preference: SupportedPreference): SupportedPreferenceState => {
      if (!excalidrawAPI) return getPreferenceState();

      const appState = excalidrawAPI.getAppState();
      switch (preference) {
        case "toolLock":
          excalidrawAPI.updateScene({
            appState: {
              activeTool: appState.activeTool.locked
                ? {
                    type: "selection",
                    customType: null,
                    lastActiveTool: null,
                    locked: false,
                  }
                : {
                    ...appState.activeTool,
                    locked: true,
                  },
            },
            captureUpdate: CaptureUpdateAction.EVENTUALLY,
          });
          break;
        case "objectsSnapMode":
          excalidrawAPI.updateScene({
            appState: {
              objectsSnapModeEnabled: !appState.objectsSnapModeEnabled,
              gridModeEnabled: false,
            },
            captureUpdate: CaptureUpdateAction.EVENTUALLY,
          });
          break;
        case "gridMode":
          excalidrawAPI.updateScene({
            appState: {
              gridModeEnabled: !appState.gridModeEnabled,
              objectsSnapModeEnabled: false,
            },
            captureUpdate: CaptureUpdateAction.EVENTUALLY,
          });
          break;
        case "zenMode":
          excalidrawAPI.updateScene({
            appState: { zenModeEnabled: !appState.zenModeEnabled },
            captureUpdate: CaptureUpdateAction.EVENTUALLY,
          });
          break;
        case "viewMode":
          excalidrawAPI.updateScene({
            appState: { viewModeEnabled: isViewModeEnforced || !appState.viewModeEnabled },
            captureUpdate: CaptureUpdateAction.EVENTUALLY,
          });
          break;
        case "canvasProperties":
          excalidrawAPI.updateScene({
            appState: {
              stats: {
                ...appState.stats,
                open: !appState.stats.open,
              },
            },
            captureUpdate: CaptureUpdateAction.EVENTUALLY,
          });
          break;
      }

      return {
        toolLock:
          preference === "toolLock" ? !appState.activeTool.locked : appState.activeTool.locked,
        objectsSnapMode:
          preference === "objectsSnapMode"
            ? !appState.objectsSnapModeEnabled
            : preference === "gridMode"
              ? false
              : appState.objectsSnapModeEnabled,
        gridMode:
          preference === "gridMode"
            ? !appState.gridModeEnabled
            : preference === "objectsSnapMode"
              ? false
              : appState.gridModeEnabled,
        zenMode: preference === "zenMode" ? !appState.zenModeEnabled : appState.zenModeEnabled,
        viewMode:
          isViewModeEnforced ||
          (preference === "viewMode" ? !appState.viewModeEnabled : appState.viewModeEnabled),
        canvasProperties:
          preference === "canvasProperties" ? !appState.stats.open : appState.stats.open,
      };
    },
    [excalidrawAPI, getPreferenceState, isViewModeEnforced],
  );

  const applyImportedScene = useCallback(() => {
    if (!pendingImportedScene || !excalidrawAPI) return;

    excalidrawAPI.updateScene({
      elements: pendingImportedScene.elements,
      appState: pendingImportedScene.appState as AppState,
    });
    setShowWelcome(false);
    dismissImportShareDialog();
  }, [dismissImportShareDialog, excalidrawAPI, pendingImportedScene]);

  const handleSubmitFeedback = useCallback(
    async (payload: { category: FeedbackCategory; description: string; screenshot?: string }) => {
      const token = await getToken();
      return backendClient.submitFeedback(token, payload);
    },
    [getToken],
  );

  // Capture the visible app surface (canvas + app UI) as a data URL. The user
  // must explicitly opt in inside the feedback dialog because the screenshot
  // may contain sensitive content. FeedbackDialog marks itself as excluded so
  // the modal is not included in the captured image.
  const handleCaptureScreenshot = useCallback(async (): Promise<string | null> => {
    if (!containerRef.current) return null;
    return captureAppScreenshot(containerRef.current);
  }, []);

  const cloudSaveContextRef = useRef<{ saving: boolean } | null>(null);
  useLayoutEffect(() => {
    const context = { saving: false };
    cloudSaveContextRef.current = context;
    createDrawingOperationIdRef.current = crypto.randomUUID();
    return () => {
      cloudSaveContextRef.current = null;
      if (context.saving) setIsSaving(false);
    };
  }, [
    excalidrawAPI,
    account?.homeAccountId,
    canonicalDrawingId,
    isNewDrawing,
    isCollaborating,
    speAvailable,
  ]);

  const handleSaveToSpe = useCallback(
    async (name: string): Promise<ApplicationResult> => {
      if (
        !excalidrawAPI ||
        !speAvailable ||
        !isNewDrawing ||
        isCollaborating ||
        isEphemeralJoin ||
        collabRef.current?.getIsEphemeral()
      ) {
        const message = "Save to cloud requires an editable local solo drawing.";
        setSaveToCloudError(message);
        return { status: "error", message };
      }
      const context = cloudSaveContextRef.current;
      if (!context || context.saving) {
        return {
          status: "error",
          message: "A cloud save is already running or the editor changed. Try again when ready.",
        };
      }
      context.saving = true;
      const isCurrentContext = () => context !== null && cloudSaveContextRef.current === context;
      const capturedScene = serializeFromAPI(excalidrawAPI);
      setSaveToCloudError(null);
      setIsSaving(true);
      setSaveStatus("saving");
      try {
        const previewPngBase64 = await createDrawingPreview(excalidrawAPI);
        const token = await getToken();
        if (!isCurrentContext()) {
          return {
            status: "error",
            message: "The drawing or account changed before saving. No cloud copy was created.",
          };
        }
        const result = await backendClient.createDrawing(
          token,
          {
            name,
            sceneJson: capturedScene,
            previewPngBase64,
          },
          createDrawingOperationIdRef.current,
        );
        if (!isCurrentContext()) {
          return {
            status: "saved_snapshot",
            drawingId: result.drawingId,
            message:
              "A cloud snapshot was saved, but the editor context changed. The current canvas was not modified.",
          };
        }
        cachePreviewFromBase64(result.drawingId, result.updatedAt, previewPngBase64);
        currentFileNameRef.current = name;
        if (serializeFromAPI(excalidrawAPI) !== capturedScene) {
          setSaveStatus("unsaved");
          setSaveToCloudError(
            "A cloud copy was saved, but newer edits remain here. Keep this canvas or open the saved copy from My Drawings.",
          );
          createDrawingOperationIdRef.current = crypto.randomUUID();
          return {
            status: "saved_snapshot",
            drawingId: result.drawingId,
            url: `${window.location.origin}/drawing/${encodeURIComponent(result.drawingId)}`,
            message:
              "The captured drawing was saved. Newer edits remain in this local canvas; opening the cloud copy would discard them.",
          };
        }
        setShowSaveToCloudDialog(false);
        localStorage.removeItem(sceneOnlyStorageKey());
        navigate(`/drawing/${encodeURIComponent(result.drawingId)}`);
        return {
          status: "saved",
          drawingId: result.drawingId,
          url: `${window.location.origin}/drawing/${encodeURIComponent(result.drawingId)}`,
        };
      } catch (err) {
        if (!isCurrentContext())
          return {
            status: "error",
            message: "Saving did not complete before the editor context changed.",
          };
        console.error("[EditorPage] SPE drawing creation failed:", err);
        setSaveStatus(shouldTreatAsOffline(err) ? "offline" : "error");
        const message =
          "The drawing could not be saved to cloud. Your local canvas has been retained.";
        setSaveToCloudError(message);
        return { status: "error", message };
      } finally {
        context.saving = false;
        if (isCurrentContext()) setIsSaving(false);
      }
    },
    [
      excalidrawAPI,
      getToken,
      navigate,
      speAvailable,
      isNewDrawing,
      isCollaborating,
      isEphemeralJoin,
    ],
  );

  const applicationDialog = useApplicationWebMCP({
    accountId: account?.homeAccountId ?? "",
    enabled: !!account && !authLoading && !ssoInProgress,
    available: backendCapabilityState === "full",
    context: {
      storage: isNewDrawing ? "local" : "cloud",
      drawingId: canonicalDrawingId ?? undefined,
      scene: sceneLoading ? "loading" : error ? "error" : "ready",
      editable:
        !!excalidrawAPI && !isViewModeEnforced && !excalidrawAPI.getAppState().viewModeEnabled,
      dirty: isDirty,
      saving: isSaving,
      saveConflict: saveConflictOpen,
      session:
        isEphemeralJoin || collabRef.current?.getIsEphemeral()
          ? "local"
          : isCollaborating
            ? "cloud"
            : "none",
    },
    unavailableMessage:
      backendCapabilityState === "local-only"
        ? "Cloud storage and live collaboration require backend access. Use the app's access-request flow; you can keep editing locally. Saving a drawing does not grant account access."
        : cloudExportDisabledReason,
    canvasKey: `${canonicalDrawingId ?? `${isEphemeralJoin ? "room" : "local"}:${searchParams.toString()}`}:${sceneLoading}:${!!error}`,
    title: currentFileNameRef.current ?? "Current drawing",
    getToken,
    check: (request) => {
      if (sceneLoading || (error && request.tool !== "drawing_open")) {
        throw new ApplicationToolError(
          "NOT_READY",
          "The current drawing is loading or unavailable. You can list cloud drawings and open another after loading finishes.",
        );
      }
      if (isSaving || saveConflictOpen) {
        throw new ApplicationToolError(
          "BUSY",
          "Resolve the current save or conflict before this action.",
        );
      }
      if (request.tool === "drawing_open") {
        if (isDirty && !isNewDrawing)
          throw new ApplicationToolError(
            "UNSAVED_CHANGES",
            "Save or resolve the current drawing's unsaved changes before opening another drawing.",
          );
        if (isCollaborating || isEphemeralJoin || collabRef.current?.getIsEphemeral())
          throw new ApplicationToolError(
            "ACTIVE_SESSION",
            "Leave the current collaboration session in the app before opening another drawing.",
          );
      } else {
        if (!excalidrawAPI || isViewModeEnforced || excalidrawAPI.getAppState().viewModeEnabled) {
          throw new ApplicationToolError("READ_ONLY", "This drawing is not editable.");
        }
        if (request.tool === "drawing_save") {
          if (
            isNewDrawing &&
            (isCollaborating || isEphemeralJoin || collabRef.current?.getIsEphemeral())
          ) {
            throw new ApplicationToolError(
              "ACTIVE_SESSION",
              "Leave the current live session in the app before saving this local drawing to cloud.",
            );
          }
          if (isNewDrawing && !request.name) {
            throw new ApplicationToolError(
              "INVALID_INPUT",
              "Saving a local drawing requires a name and a solo canvas.",
            );
          }
          if (!isNewDrawing && request.name !== undefined) {
            throw new ApplicationToolError(
              "INVALID_INPUT",
              "Saving an existing cloud drawing does not rename it. Omit name.",
            );
          }
        } else if (request.action === "start_session") {
          if (!isNewDrawing) {
            throw new ApplicationToolError(
              "INVALID_INPUT",
              "start_session is for local live sharing. Use create for a cloud people link.",
            );
          }
          if (isEphemeralJoin && !isEphemeralRoomOwner) {
            throw new ApplicationToolError(
              "ACCESS_DENIED",
              "Only the local room starter can provide invitations.",
            );
          }
        } else if (!canonicalDrawingId || drawingRole !== "owner") {
          throw new ApplicationToolError(
            "ACCESS_DENIED",
            "Only the owner of a cloud drawing can create people links. Save a local drawing to cloud first.",
          );
        }
      }
    },
    execute: async (request) => {
      if (request.tool === "drawing_open") {
        if (isNewDrawing && excalidrawAPI) {
          if (!writeSceneStorage(sceneOnlyStorageKey(), serializeFromAPI(excalidrawAPI))) {
            throw new ApplicationToolError(
              "DRAFT_NOT_SAVED",
              "The local draft could not be preserved. Resolve browser storage or export it before opening another drawing.",
            );
          }
          if (sceneOnlySaveTimerRef.current) {
            clearTimeout(sceneOnlySaveTimerRef.current);
            sceneOnlySaveTimerRef.current = null;
          }
        }
        navigate(`/drawing/${encodeURIComponent(request.drawingId)}`, {
          state: { appUsageAutomated: true },
        });
        return {
          status: "opening",
          drawingId: request.drawingId,
          message:
            "Navigation requested. The app will check access and load the drawing; opening is not yet confirmed.",
        };
      }
      if (request.tool === "drawing_save") {
        if (isNewDrawing) return handleSaveToSpe(request.name!);
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
        const saved = await requestCollaborativeSave();
        return {
          status: saved ? "saved" : "not_saved",
          drawingId: canonicalDrawingId ?? undefined,
          message: saved
            ? "The current drawing was saved."
            : "The current scene is not confirmed saved. Check the save indicator or conflict dialog.",
        };
      }
      if (request.action === "start_session") {
        if (!collabRef.current) await handleStartSession();
        const url = ephemeralShareUrlForRole(request.role);
        if (!url) {
          throw new ApplicationToolError(
            "SESSION_NOT_READY",
            "A shareable local session is not ready. Check collaboration status in the app before retrying; no cloud file was created.",
          );
        }
        const connected = collabRef.current?.getIsConnected() === true;
        return {
          status: connected ? "shared" : "session_starting",
          url,
          role: request.role,
          message: connected
            ? "Temporary live-session invitation ready. No cloud file was created and no message was sent."
            : "Temporary invitation prepared, but the live connection is not confirmed. Read sharing state to check connection status. No cloud file was created.",
        };
      }
      const url = await resolveCloudShareUrl(request.role);
      return {
        status: "shared",
        drawingId: canonicalDrawingId ?? undefined,
        url,
        role: request.role,
        message: "People link ready. No invitation or message was sent.",
      };
    },
    shareState: async () => {
      if (sceneLoading || error) {
        throw new ApplicationToolError(
          "NOT_READY",
          "The current drawing is loading or unavailable. Wait or open another drawing before reading its sharing state.",
        );
      }
      if (isNewDrawing) {
        const collab = collabRef.current;
        const links = (["viewer", "editor"] as const).flatMap((role) => {
          const url = ephemeralShareUrlForRole(role);
          return url ? [{ role, url }] : [];
        });
        return {
          status: "ok",
          kind: "live_session",
          connected: collab?.getIsConnected() === true,
          isShared: links.length > 0,
          links,
          message:
            "Temporary live invitations are available only to the room starter. No session was created by this read.",
        };
      }
      if (!canonicalDrawingId || drawingRole !== "owner") {
        throw new ApplicationToolError(
          "ACCESS_DENIED",
          "People links are available only to the current cloud drawing's owner.",
        );
      }
      const token = await getToken();
      const state = await backendClient.getDrawingShare(token, canonicalDrawingId);
      return {
        status: "ok",
        isShared: state.isShared,
        links: state.links.map(({ role, shareUrl }) => ({ role, url: shareUrl })),
      };
    },
  });

  // -----------------------------------------------------------------------
  // Keyboard shortcut: Ctrl+S → save
  // -----------------------------------------------------------------------
  // Ctrl+S: no-op in Scene-Only mode (rule 21), saves in File-Backed mode (rule 22)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        // Don't intercept when user is typing in a dialog input/textarea/select
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        e.preventDefault();
        handleManualSave();
      }
    };
    // Use capture phase so our handler fires before Excalidraw's internal
    // keyboard dispatcher (which calls stopPropagation on CtrlOrCmd+S).
    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true });
  }, [handleManualSave]);

  useEffect(() => {
    const handleOffline = () => setSaveStatus("offline");
    const handleOnline = () => {
      if (isDirty && !isSaving && excalidrawAPI && driveId && itemId) {
        void handleSave();
      } else if (saveStatus === "offline") {
        setSaveStatus(isDirty ? "unsaved" : "saved");
      }
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [driveId, itemId, excalidrawAPI, handleSave, isDirty, isSaving, saveStatus]);

  // -----------------------------------------------------------------------
  // beforeunload: warn + emergency localStorage backup
  // -----------------------------------------------------------------------
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (skipBeforeUnloadRef.current || !canWriteSceneStorage()) return;
      if (isDirty && excalidrawAPI && driveId && itemId) {
        e.preventDefault();
        e.returnValue = "";
        // Emergency backup to localStorage
        writeSceneStorage(recoveryStorageKey(driveId, itemId), serializeFromAPI(excalidrawAPI));
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty, excalidrawAPI, driveId, itemId]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (authLoading || ssoInProgress) {
    return <CenteredMessage>Signing in…</CenteredMessage>;
  }

  if (!account) {
    return (
      <main
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          boxSizing: "border-box",
          padding: 24,
          background: "var(--color-bg)",
          color: "var(--text-primary-color)",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", width: "100%", maxWidth: 720, minWidth: 0 }}>
          <h1
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 12,
              fontSize: 28,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: "0 0 16px",
            }}
          >
            <BrandIcon size={32} />
            <span>Excalidraw for Microsoft 365</span>
          </h1>
          <p
            style={{
              color: "var(--color-text-secondary)",
              fontSize: 18,
              fontWeight: 400,
              lineHeight: 1.5,
              margin: "0 0 24px",
            }}
          >
            Sketch ideas, explore concepts, and collaborate with your team.
          </p>
          <Button
            variant="primary"
            onClick={signIn}
            style={{
              padding: "12px 32px",
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 8,
              maxWidth: "100%",
              whiteSpace: "normal",
            }}
          >
            Sign in with Microsoft
          </Button>
        </div>
      </main>
    );
  }

  const fullAccessDialog = showFullAccess && (
    <FullAccessDialog
      key={account?.homeAccountId}
      state={backendCapabilityState}
      phase={backendAccessPhase}
      onRetry={retryBackendAccess}
      onClose={closeFullAccess}
    />
  );

  if (!isNewDrawing && (backendAccessLoading || backendCapabilityState === "checking")) {
    return (
      <CenteredMessage>
        {backendAccessPhase === "preparing-cloud"
          ? "Preparing cloud features…"
          : "Checking Excalidraw-MS access…"}
        {fullAccessDialog}
      </CenteredMessage>
    );
  }

  if (
    !isNewDrawing &&
    (backendCapabilityState === "local-only" ||
      backendCapabilityState === "temporarily-unavailable")
  ) {
    return (
      <CenteredMessage>
        <BackendAccessDeniedNotice
          state={backendCapabilityState}
          layout="page"
          onRetry={retryBackendAccess}
          onContinue={() => navigate("/")}
          onSignOut={() => void handleSignOut()}
          onGetFullAccess={openFullAccess}
        />
        {fullAccessDialog}
      </CenteredMessage>
    );
  }

  if (sceneLoading) {
    return <CenteredMessage>Loading drawing…</CenteredMessage>;
  }

  if (error) {
    const invalidScene = error.kind === "invalid-scene";
    return (
      <DrawingLoadErrorView
        invalidScene={invalidScene}
        message={error.message}
        diagnostic={error.diagnostic}
        copyDetailsError={copyDetailsError ?? undefined}
        canDownloadOriginal={!!invalidSceneRecovery}
        canRepairOrder={!!invalidSceneRecovery?.canRepairOrder}
        onBack={() => navigate("/")}
        onCopyAiRepairPrompt={handleCopyInvalidSceneAiRepairPrompt}
        onExportForRepair={handleDownloadInvalidScene}
        onOpenRepairedCopy={handleOpenRepairedCopy}
        onRetry={() => setSceneLoadAttempt((attempt) => attempt + 1)}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
    >
      {applicationDialog}
      <div style={{ flex: 1, minHeight: 0 }}>
        <Excalidraw
          aiEnabled={false}
          excalidrawAPI={onExcalidrawAPI}
          initialData={initialData ?? undefined}
          theme={resolvedTheme}
          isCollaborating={isCollaborating}
          viewModeEnabled={isViewModeEnforced ? true : undefined}
          UIOptions={{
            canvasActions: {
              saveToActiveFile: false,
              export: {
                saveFileToDisk: true,
                onExportToBackend: hasBackendAccess ? handleExportToLink : undefined,
                renderCustomUI:
                  showDisabledShareableLink || showSaveToCloudCard
                    ? () => (
                        <>
                          {showDisabledShareableLink && (
                            <ShareableLinkExportCard disabledReason={cloudExportDisabledReason} />
                          )}
                          {showSaveToCloudCard && (
                            <SaveToCloudExportCard
                              onSelect={openSaveToCloudDialog}
                              disabled={backendCapabilityState !== "full"}
                              disabledReason={cloudExportDisabledReason}
                            />
                          )}
                        </>
                      )
                    : undefined,
              },
            },
          }}
          onChange={handleChange}
          onPointerUpdate={handlePointerUpdate}
          renderTopRightUI={() => (
            <div
              className="exms-editor-toolbar"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                pointerEvents: "auto",
                position: "relative",
                zIndex: 10000,
              }}
            >
              <HomeButton onClick={() => guardedNavigateAwayRef.current()} />
              <ShareButton
                isCollaborating={isCollaborating}
                onClick={() => {
                  void handleOpenCollabShareDialog();
                }}
                activeCount={roomActiveCount}
                disabled={backendCapabilityState !== "full" && !showSaveToCloudCard}
                disabledReason={
                  backendCapabilityState === "local-only"
                    ? "Sharing requires Excalidraw-MS access — Join the Excalidraw-MS group to unlock collaboration"
                    : backendCapabilityState === "temporarily-unavailable"
                      ? "Cloud sharing is temporarily unavailable — try again shortly"
                      : backendCapabilityState === "checking"
                        ? "Checking Excalidraw-MS access…"
                        : undefined
                }
              />
              {isFileBacked && (
                <span className="exms-save-status">
                  <SaveStatusIndicator status={saveStatus} onRetry={handleSave} />
                </span>
              )}
              <button className="ai-toolbar-trigger" onClick={openAiChooser} style={aboutBtnStyle}>
                Create with AI
              </button>
              <button
                className="exms-toolbar-secondary"
                onClick={() => setShowFeedbackDialog(true)}
                data-app-usage-surface="feedback"
                title="Feedback"
                aria-label="Feedback"
                style={feedbackBtnStyle}
              >
                Feedback
              </button>
              <button
                className="exms-toolbar-secondary"
                onClick={() => setShowAboutDialog(true)}
                title="About"
                aria-label="About"
                style={aboutBtnStyle}
              >
                About
              </button>
              <UserProfileMenu
                account={account!}
                onSignOut={handleSignOut}
                themePreference={themePreference}
                onThemePreferenceChange={setThemePreference}
                canViewImpactDashboard={canViewImpactDashboard}
                onOpenImpactDashboard={() => navigate("/dashboard")}
              />
            </div>
          )}
        >
          <CommandPalette />
          <EditorSidePanel />
          <CustomMainMenu
            account={account}
            sceneMode={sceneMode}
            isCollaborating={isCollaborating}
            backendFeaturesEnabled={backendCapabilityState === "full"}
            isLocalOnly={backendCapabilityState === "local-only"}
            onGetFullAccess={openFullAccess}
            backendFeaturesUnavailable={
              backendCapabilityState === "checking" ||
              backendCapabilityState === "temporarily-unavailable"
            }
            onSave={isFileBacked && drawingRole !== "viewer" ? handleManualSave : undefined}
            saveDisabled={isSaving}
            onOpenLiveCollab={() => {
              void handleOpenCollabShareDialog();
            }}
            onNewDrawing={guardedNavigateAway}
            onSignOut={handleSignOut}
            onAbout={() => setShowAboutDialog(true)}
            onConnectAi={openAiChooser}
            onFeedback={() => setShowFeedbackDialog(true)}
            onDevTools={devToolsAvailable ? () => setShowDevToolsPanel(true) : undefined}
            getPreferenceState={getPreferenceState}
            onTogglePreference={handleTogglePreference}
            preferencesEditable={!isViewModeEnforced}
            themePreference={themePreference}
            onThemePreferenceChange={setThemePreference}
          />
          {isNewDrawing && showWelcome && (
            <CustomWelcomeScreen
              aiIntroduction={
                account
                  ? {
                      accountId: account.homeAccountId,
                      getToken,
                      access: backendCapabilityState,
                      onSetup: () =>
                        backendCapabilityState === "local-only" ? openFullAccess() : openAiSetup(1),
                      onExample: () => openAiSetup(2),
                    }
                  : undefined
              }
              galleryTabs={cloudFeaturesVisible ? galleryTabs : undefined}
              galleryActiveTab={galleryData.activeTab}
              onGalleryTabChange={galleryData.setActiveTab}
              galleryItems={galleryData.items}
              onGalleryItemClick={handleGalleryItemClick}
              onGalleryItemDelete={(item) => void handleGalleryItemDelete(item)}
              onGalleryItemDuplicate={
                speAvailable ? (item) => void handleGalleryItemDuplicate(item) : undefined
              }
              onGalleryItemRetryCopy={(item) => void handleGalleryItemDuplicate(item)}
              onGalleryItemDismissCopy={handleGalleryItemDismissCopy}
              onGalleryItemShare={setGalleryShareTarget}
              onGalleryItemRename={handleGalleryItemRename}
              aiStatusKnownDrawingIds={galleryData.aiStatusKnownDrawingIds}
              aiGrantedDrawingIds={galleryData.aiGrantedDrawingIds}
              aiGrantsByDrawingId={galleryData.aiGrantsByDrawingId}
              galleryLoading={backendCapabilityState === "checking" || galleryData.loading}
              galleryLoadingMore={galleryData.loadingMore}
              galleryHasMore={galleryData.hasMore}
              onGalleryLoadMore={galleryData.loadMore}
              onGalleryItemVisible={galleryData.onItemVisible}
              galleryError={
                backendCapabilityState === "temporarily-unavailable"
                  ? "Cloud drawings are temporarily unavailable."
                  : galleryData.error
              }
              onGalleryRetry={
                backendCapabilityState === "temporarily-unavailable"
                  ? retryBackendAccess
                  : galleryData.retry
              }
            />
          )}
        </Excalidraw>
      </div>
      <DataClassificationBanner />
      {(backendCapabilityState === "local-only" ||
        backendCapabilityState === "temporarily-unavailable") && (
        <BackendAccessDeniedNotice
          state={backendCapabilityState}
          onRetry={retryBackendAccess}
          onGetFullAccess={openFullAccess}
        />
      )}
      {announcement && (
        <AnnouncementBanner announcement={announcement} onDismiss={dismissAnnouncement} />
      )}
      {recoveryBackup && (
        <RecoveryDialog onRestore={handleRestoreRecovery} onDiscard={handleDiscardRecovery} />
      )}
      {saveConflictOpen && !isCollaborating && (
        <SaveConflictDialog
          busy={saveConflictBusy}
          error={saveConflictError}
          onReload={() => {
            skipBeforeUnloadRef.current = true;
            window.location.reload();
          }}
          onOverwrite={() => void handleOverwriteSaveConflict()}
          onClose={() => {
            if (!saveConflictBusy) setSaveConflictOpen(false);
          }}
        />
      )}
      {showCollabShareDialog && !showDrawingAiRevoke && !showAiConnectDialog && !showAiChooser && (
        <CollabShareDialog
          drawingTitle={isFileBacked ? currentFileNameRef.current : undefined}
          sceneMode={sceneMode}
          isCollaborating={isCollaborating}
          userName={account?.name ?? account?.username ?? "Signed in user"}
          shareUrl={isCloudRecipient ? drawingReference.referenceUrl : activeShareUrl}
          linkKind={isCloudRecipient ? "existing-access" : "sharing"}
          sharedWithPeople={isFileBacked && !sceneLoading ? hasPeopleSharing : undefined}
          peopleSharingKind={peopleSharingKind}
          peopleAccessLoading={
            isFileBacked &&
            (sceneLoading || (isCloudRecipient ? drawingReference.loading : peopleAccessLoading))
          }
          peopleAccessError={isCloudRecipient ? drawingReference.error : peopleAccessError}
          onRetryPeopleAccess={isCloudRecipient ? drawingReference.retry : retryPeopleAccess}
          peopleAccessRetryLabel={peopleAccessRetryLabel}
          canCreateShareLink={canCreateLiveShareLink}
          hasReusablePeopleLink={shareUrlByRoleRef.current.size > 0}
          linkRole={linkRole}
          linkBusy={linkRoleBusy}
          linkError={linkError}
          stopCase={stopCase}
          onLinkRoleChange={handleLinkRoleChange}
          onStartSession={handleStartSessionFromDialog}
          startSessionDisabled={backendCapabilityState !== "full"}
          startSessionDisabledReason={
            backendCapabilityState === "temporarily-unavailable"
              ? "Cloud features are temporarily unavailable."
              : backendCapabilityState === "checking"
                ? "Preparing cloud features…"
                : undefined
          }
          onStopSession={handleStopSession}
          onCopyLink={handleCopyActiveLink}
          onShareWithAi={
            isFileBacked
              ? async () => {
                  if (isCloudRecipient) await drawingReference.check();
                  await drawingAiSharing.share();
                }
              : undefined
          }
          onSaveToCloudForAi={
            cloudFeaturesVisible && !isFileBacked
              ? () => {
                  setShowCollabShareDialog(false);
                  openSaveToCloudDialog();
                }
              : undefined
          }
          saveToCloudForAiDisabled={!speAvailable}
          saveToCloudForAiReason={
            backendCapabilityState === "temporarily-unavailable"
              ? "Cloud features are temporarily unavailable."
              : backendCapabilityState === "checking"
                ? "Preparing cloud features…"
                : undefined
          }
          aiCode={drawingAiSharing.grant?.code}
          aiAccess={
            isCloudRecipient
              ? drawingReference.access
              : drawingAiSharing.grant
                ? (drawingAiSharing.grant.access ?? undefined)
                : sceneLoading
                  ? undefined
                  : { role: drawingRole, canEdit: drawingRole !== "viewer" }
          }
          aiLoading={drawingAiSharing.loading && !drawingAiSharing.preparing}
          aiPreparing={drawingAiSharing.preparing}
          aiError={drawingAiSharing.error}
          aiConnectionStatus={
            !isCollaborating || !sponsorOid || !participantsConfirmed
              ? "unconfirmed"
              : ownAiParticipants.some((participant) => participant.activity !== "idle")
                ? "connected"
                : ownAiParticipants.length > 0
                  ? "idle"
                  : "not-connected"
          }
          onRequestStopAiAccess={() => {
            setDrawingAiRevokeError(null);
            setShowDrawingAiRevoke(true);
          }}
          onConnectAiAssistant={() => openAiSetup(1)}
          onClose={() => setShowCollabShareDialog(false)}
        />
      )}
      {showDrawingAiRevoke && (
        <DestructiveConfirmationDialog
          title="Stop your AI access?"
          testId="stop-drawing-ai-dialog"
          confirmLabel="Stop AI access"
          busyLabel="Stopping AI access…"
          busy={drawingAiRevoking}
          error={drawingAiRevokeError}
          blocking
          onConfirm={() => void handleRevokeDrawingAi()}
          onClose={() => setShowDrawingAiRevoke(false)}
        >
          <p style={{ margin: 0 }}>
            Your assistants will disconnect from this drawing and this code will stop working.
            Changes already made stay in the drawing. Other people&apos;s assistants are not
            affected.
          </p>
        </DestructiveConfirmationDialog>
      )}
      {shareableLinkUrl && !showCollabShareDialog && (
        <ShareableLinkDialog link={shareableLinkUrl} onClose={() => setShareableLinkUrl(null)} />
      )}
      {showLoadFromLinkDialog && (
        <LoadFromLinkDialog
          behind={nativeDialogActive}
          onReplace={applyImportedScene}
          onExportAsImage={openImageExportDialog}
          onSaveToDisk={saveToDisk}
          onClose={dismissImportShareDialog}
        />
      )}
      {showAboutDialog && <AboutDialog onClose={() => setShowAboutDialog(false)} />}
      {fullAccessDialog}
      {showAiChooser && (
        <CreateWithAiDialog
          onGetFullAccess={openFullAccess}
          localOnly={backendCapabilityState === "local-only"}
          onClose={closeAiChooser}
          onSetup={() => openAiSetup(1)}
          onExample={() => openAiSetup(2)}
          agency={{
            onSetup: () => openAiSetup(1, "agency"),
            onExample: () => openAiSetup(2, "agency"),
          }}
        />
      )}
      {showAiConnectDialog && (
        <AiSetupDialog
          key={`${aiSetupHost}-${aiInitialStep}`}
          initialStep={aiInitialStep}
          onBack={openAiChooser}
          onClose={closeAiSetup}
        />
      )}
      {showSaveToCloudDialog && (
        <SaveToCloudDialog
          saving={isSaving}
          error={saveToCloudError}
          onSave={(name) => void handleSaveToSpe(name)}
          onClose={() => setShowSaveToCloudDialog(false)}
        />
      )}
      {drawingPendingRename && (
        <RenameDrawingDialog
          key={drawingPendingRename.drawingId}
          currentTitle={drawingPendingRename.title}
          renaming={drawingRenaming}
          error={drawingRenameError}
          onRename={(name) => void handleConfirmDrawingRename(name)}
          onClose={() => {
            setDrawingRenameError(null);
            setDrawingPendingRename(null);
          }}
        />
      )}
      {drawingPendingDelete && (
        <DestructiveConfirmationDialog
          title="Delete drawing?"
          confirmLabel="Delete"
          busyLabel="Deleting…"
          busy={drawingDeleteState === "deleting"}
          error={drawingDeleteError}
          testId="delete-drawing-dialog"
          onConfirm={() => void handleConfirmDrawingDelete()}
          onClose={() => {
            setDrawingDeleteError(null);
            setDrawingPendingDelete(null);
          }}
        >
          <p style={{ margin: 0 }}>
            Delete &ldquo;{drawingPendingDelete.title}&rdquo;? This cannot be undone.
          </p>
        </DestructiveConfirmationDialog>
      )}
      {galleryShareTarget && !showAiConnectDialog && !showAiChooser && (
        <GalleryShareDialog
          key={galleryShareTarget.drawingId}
          drawing={galleryShareTarget}
          accountId={account?.homeAccountId ?? account?.username}
          getToken={getToken}
          onPeopleAccessResolved={galleryData.setPeopleSharingStatus}
          onAiAccessResolved={galleryData.setAiGrantStatus}
          onAiShared={galleryData.addAiGrant}
          onAiRevoked={galleryData.removeAiGrant}
          onConnect={() => openAiSetup(1)}
          onClose={() => {
            if (!showAiConnectDialog) setGalleryShareTarget(null);
          }}
        />
      )}
      {aiShareTarget && !showAiConnectDialog && !showAiChooser && (
        <ShareWithAiDialog
          title={aiShareTarget.title}
          code={
            aiShareResult?.code ??
            galleryData.aiGrantsByDrawingId.get(aiShareTarget.drawingId)?.code ??
            aiShareTarget.aiGrantCode ??
            null
          }
          access={
            aiShareResult?.access ??
            galleryData.aiGrantsByDrawingId.get(aiShareTarget.drawingId)?.access ??
            aiShareTarget.aiGrantAccess ?? {
              // Not yet shared: we already know the user's role on the drawing,
              // and that role is exactly the ceiling on the assistant.
              role: aiShareTarget.role,
              canEdit: aiShareTarget.role === "owner" || aiShareTarget.role === "editor",
            }
          }
          onShare={() => handleShareAi(aiShareTarget)}
          onConnect={() => openAiSetup(1)}
          onRevoke={() => {
            // Revocation is terminal, so it keeps its own confirmation. Close
            // this dialog first rather than stacking two overlays.
            const code =
              aiShareResult?.code ??
              galleryData.aiGrantsByDrawingId.get(aiShareTarget.drawingId)?.code;
            handleGalleryItemRevokeAi({ ...aiShareTarget, aiGrantCode: code });
            setAiShareTarget(null);
            setAiShareResult(null);
          }}
          onClose={() => {
            // Retain the prepared sharing state while AI setup is open.
            if (showAiConnectDialog) return;
            setAiShareTarget(null);
            setAiShareResult(null);
          }}
        />
      )}
      {aiGrantPendingRevoke && (
        <DestructiveConfirmationDialog
          title="Stop AI access?"
          confirmLabel="Stop AI access"
          busyLabel="Stopping…"
          busy={aiGrantRevokeState === "revoking"}
          error={aiGrantRevokeError}
          testId="revoke-ai-grant-dialog"
          onConfirm={() => void handleConfirmAiGrantRevoke()}
          onClose={() => {
            setAiGrantRevokeError(null);
            setAiGrantPendingRevoke(null);
          }}
        >
          <p style={{ margin: 0 }}>
            Stop AI access to &ldquo;{aiGrantPendingRevoke.title}&rdquo;? The code{" "}
            <strong>{aiGrantPendingRevoke.aiGrantCode}</strong> stops working and cannot be turned
            back on — sharing again later creates a new code. Anything an assistant already opened
            is unaffected.
          </p>
        </DestructiveConfirmationDialog>
      )}
      {showFeedbackDialog && (
        <FeedbackDialog
          onClose={() => setShowFeedbackDialog(false)}
          onSubmit={handleSubmitFeedback}
          teamsChatUrl={FEEDBACK_TEAMS_CHAT_URL}
          onCaptureScreenshot={handleCaptureScreenshot}
          backendCapabilityState={backendCapabilityState}
        />
      )}
      <DevToolsPanel open={showDevToolsPanel} onClose={() => setShowDevToolsPanel(false)} />
      {showUnsavedDialog && (
        <UnsavedChangesDialog
          isFileBacked={isFileBacked}
          onAction={(action) => void handleUnsavedAction(action)}
          saving={unsavedSaving}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper component
// ---------------------------------------------------------------------------

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "100vh",
        fontFamily: "system-ui, sans-serif",
        fontSize: 18,
      }}
    >
      <div style={{ textAlign: "center" }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// About button styles (top-right bar)
// ---------------------------------------------------------------------------

const aboutBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 999,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--exms-toolbar-hover, var(--island-bg-color))",
  color: "var(--text-primary-color)",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

const feedbackBtnStyle: React.CSSProperties = {
  ...aboutBtnStyle,
};
